/**
 * Murphy Door Configurator - Blueprint / Measurement Overlay (PHASE 2)
 *
 * PURPOSE
 * Owns measurement mode: the tape-measure toggle bottom-left of the configurator
 * image, the layer isolation it performs, and the dimension drawing it renders.
 *
 * MODULE TYPE: ui- (Official DOM manipulator per CCP-RULES.md violation #4
 * exception). Direct DOM work is authorized here; deriving a MEASUREMENT is not
 * -- every number comes from md-config-bl-dimension-calculator.js via the
 * conductor. This module draws, it does not calculate.
 *
 * ---------------------------------------------------------------------------
 * WHY SVG AT RUNTIME AND NOT ANOTHER BAKED PNG
 * ---------------------------------------------------------------------------
 * The 90 existing overlays are one PNG per variant, which cannot express a
 * dimension that depends on two categories at once (casing-to-casing is Width x
 * Casing; floor-to-entablature is Height x Entablature x Swing). Baking those
 * is ~18,000 files.
 *
 * The second reason is legibility, and it is the one that actually bit us. A
 * baked label scales with the image: the existing 68px numeral renders at 11.9
 * CSS px on a 358px-wide phone and its 36px sub-label at 6.3px -- already below
 * this theme's own 12px --ui-micro-font-size floor, before adding a single new
 * callout. See scaleText(): SVG text is re-sized against the live container so
 * a label is 14 REAL pixels at every viewport. That is the whole ballgame.
 *
 * ---------------------------------------------------------------------------
 * AUTO-ENGAGE, AND WHY MANUAL WINS
 * ---------------------------------------------------------------------------
 * Opening a measurement category (Width, Height, Door Depth, Jamb Depth, Door
 * Handing) turns the mode on; opening anything else turns it off. But an
 * explicit tap sets a manual override that SURVIVES navigation between
 * measurement categories, and is cleared only by leaving the group entirely.
 *
 * The alternative -- auto always wins -- re-engages a mode the customer just
 * dismissed, every time they touch the next size control. That reads as the UI
 * fighting them, and it is the variant that gets reported as a bug.
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = (CONSTANTS && CONSTANTS.EVENTS) || {};

  const isDev = () =>
    (window.MurphyDoorConfigurator.utils &&
      window.MurphyDoorConfigurator.utils.isDevelopment &&
      window.MurphyDoorConfigurator.utils.isDevelopment()) ||
    false;

  const SVG_NS = 'http://www.w3.org/2000/svg';

  /** Label size in REAL screen pixels, held constant across every viewport. */
  const LABEL_PX = 15;
  const SUBLABEL_PX = 11;
  const LEADER_PX = 14;
  const LEADER_DETAIL_PX = 11;

  /**
   * Below this container width only priority<=2 callouts draw.
   *
   * 🚨 CALIBRATED AGAINST THE MEASURED CONTAINER, NOT THE VIEWPORT. The
   * configurator image is constrained by the product grid column, so it is
   * 435px at a 1920x1200 desktop and 370px at a 390px phone — it barely moves.
   * The first value here was 620, chosen from an imagined viewport width, which
   * put EVERY desktop permanently in compact mode and would have silently
   * dropped half the dimension chains the moment the trim numbers arrive.
   * Re-measure before changing this; do not reason about it from screen size.
   *
   * The retired LEADER_MIN_WIDTH_PX made the same mistake worse: at 620 it gated
   * the margin annotations OFF on every screen, because the container tops out
   * at 435. Six callouts were dead code no unit test could catch, because they
   * only fail in a real layout.
   */
  const COMPACT_BREAKPOINT_PX = 400;

  /* Chip measuring-tape affordance (drawChipTapeIcon). A share of the fitted
     side so it tracks the drawing, clamped so it neither disappears on the
     shortest chip (~124px) nor crowds the tallest (~170px). */
  const CHIP_TAPE_FRACTION = 0.26;
  const CHIP_TAPE_MIN_PX = 28;
  const CHIP_TAPE_MAX_PX = 48;
  const COMPACT_MAX_PRIORITY = 2;

  /**
   * Ghost-click guard window.
   *
   * The click-doubling chased on 2026-09-02 turned out to be the devtools click
   * tool (it doubles only when it must scroll the element into view first;
   * measured 1:1 once already on screen, and the Front/Back control never
   * doubled). So this is NOT patching that.
   *
   * It is here because this theme has a documented history of genuine double
   * activation on touch: Klaviyo ships a FastClick-style `needsclick` layer, and
   * `snippets/klaviyo-close-tap-fix.liquid` exists precisely because synthetic
   * taps on SVG children misbehave on iOS Safari. A FastClick-style library
   * fires a synthetic click on touchend AND the browser fires the real one
   * ~300ms later — which on a two-state switch means the customer taps once and
   * nothing appears to happen, because it toggled twice.
   *
   * 350ms is above the ~300ms synthetic-click delay and far below any interval a
   * customer could intend as two separate toggles.
   */
  const GHOST_CLICK_WINDOW_MS = 350;

  const state = {
    initialized: false,
    active: false,
    manualOverride: null, // null = follow auto; true/false = user's explicit choice
    lastCategoryWasMeasurement: false,
    // True for the span of a bulk restoration (?config= URL, Will It Fit
    // prefill, simplified-mode / gate-card defaults) — see
    // handleBulkApplyStarted/Completed below.
    bulkRestoreActive: false,
    // Count of in-flight md-config-ui-horizontal-slider.js bootstrap seeds
    // (seedSliderDefault() silently "clicking" Use Default for Width/Height).
    // A counter, not a boolean, because Width and Height each seed
    // independently and their windows can overlap. See
    // handleDefaultSeedStarted/Completed below.
    pendingDefaultSeeds: 0,
    view: 'front',
    /**
     * The `diagram` block from md-config-bl-space-calculator's last completed
     * run. Cached rather than requested: that module publishes on every
     * selection change anyway, and asking for it synchronously would mean
     * reaching into a peer business-logic module, which CCP does not allow.
     */
    spaceData: null,
    /** Which of the two drawings is showing. */
    frame: 'elevation',
    /**
     * Last-known callout value signatures, keyed by callout id -- the
     * baseline computeChangedCalloutIds() diffs against to know which
     * measurements the customer's last click actually changed, so
     * drawLabels() can light up a change-marker on exactly those. `null`
     * means "no real baseline yet" (the render about to run is a fresh
     * look -- mode just activated, or the very first render), which tells
     * computeChangedCalloutIds() to highlight nothing and just prime
     * itself for next time. See setActive() for where this resets.
     *
     * 🚨 UPDATED BY MERGE, NEVER BY REPLACEMENT -- see
     * computeChangedCalloutIds' commit step. A render's own `callouts` is
     * not a complete inventory of every id this module has ever drawn (a
     * transient state upstream can render zero usable callouts entirely,
     * and compact mode drops low-priority ones) -- replacing the whole
     * baseline with just THIS render's ids would erase history for every id
     * this render didn't happen to include, so the next real render of that
     * id would look "new" instead of "changed" and never get a marker.
     */
    calloutSignatures: null,
    /**
     * Bumped every time calloutSignatures is hard-reset (see setActive()).
     * A commit scheduled by a render from an OLDER epoch must never merge
     * into a newer one's baseline -- that would leak pre-reset data back in
     * through the merge this field's sibling doc comment describes, undoing
     * the "fresh look" the reset exists to guarantee. See
     * computeChangedCalloutIds' epoch check.
     */
    calloutEpoch: 0,
    /**
     * Wall-clock ms.now() a callout id's value was last detected as changed,
     * keyed by id. Entries older than CHANGE_MARKER_DURATION_MS are pruned
     * on every computeChangedCalloutIds() call.
     *
     * 🚨 WHY THIS EXISTS -- diffing against a constantly-refreshing baseline
     * (calloutSignatures) is NOT enough to keep a marker visible, because
     * render() always tears the SVG down and rebuilds it, and a single
     * customer action can legitimately trigger a STAGGERED sequence of
     * renders spread across real, non-microtask time (verified live:
     * SELECTION_APPLIED's own render, then BUSINESS_SPACE_CALCULATION_
     * COMPLETED's follow-up render(s), landing anywhere from ~50ms to
     * ~500ms+ apart -- see the module's own render() doc comment). Each of
     * those renders' commit resolves (via its microtask) well before the
     * NEXT one runs, so by the time render N+1's diff looks at `prev`, it
     * is comparing against the value render N JUST committed -- not the
     * value from before the customer's click. Every render after the first
     * would then correctly see "no change since the last render" and draw
     * nothing, and since render() always rebuilds from scratch, the LAST
     * render in the sequence -- the one whose DOM actually reaches the
     * customer -- is exactly the one with nothing to show.
     *
     * Tracking "when did this id last genuinely change" independently of
     * the render cycle fixes this: ANY render that happens within
     * CHANGE_MARKER_DURATION_MS of a real change re-draws that id's marker,
     * however many renders occur in between and however far apart in real
     * time they land. See drawLabels' use of the elapsed-ms this produces
     * for how a freshly-created marker element visually resumes mid-
     * animation instead of restarting on every redraw.
     */
    calloutChangedAt: {},
  };

  const cleanupHandlers = [];
  let _button = null;
  let _resizeObserver = null;
  let _viewObserver = null;
  let _lastToggleAt = 0;
  let _framesEl = null;
  let _frameSwitch = null;

  // ==========================================================================
  // DOM helpers
  // ==========================================================================

  const viewContainer = (view) =>
    document.querySelector(
      view === 'back'
        ? '.murphy-door-configurator__back-view'
        : '.murphy-door-configurator__front-view'
    );

  /**
   * The container's LAYOUT size, ignoring any zoom transform currently in
   * effect.
   *
   * 🚨 NEVER MEASURE `container` WITH getBoundingClientRect() DIRECTLY.
   * `container` (front-view/back-view) is a child of
   * `.murphy-door-configurator__image-layers`, the exact element
   * media-gallery.js's ZoomController scales via CSS `transform` while the
   * customer pinches/scrolls/double-taps (up to 3.5x). getBoundingClientRect()
   * reflects that transform, so a render() that fires while zoomed bakes a
   * font-size calibrated to the CURRENT zoom level into permanent SVG
   * attributes -- there is no ZOOM_CHANGED/ZOOM_RESET subscriber here, so
   * zooming back out afterward does nothing and the dimension text is left
   * exactly as small as the zoomed-in capture made it. Reported live: zoom to
   * max, apply a swing direction (auto-engages measurement mode and renders
   * mid-zoom), flip to the birdseye/isometric frame, then zoom back out -- the
   * callouts are unreadably tiny and stay that way.
   *
   * offsetWidth/offsetHeight are layout-box values and are NOT affected by
   * `transform` -- the same fix media-gallery.js's own ZoomController already
   * relies on for its cached naturalWidth/naturalHeight (see its constructor
   * comment: "getBoundingClientRect() returns scaled values when a CSS
   * transform is active"). `container` itself can't supply this directly --
   * front-view/back-view carry no explicit width (section-md-configurator.css)
   * and compute near 0 -- so the natural size is read from the transformed
   * ancestor instead, which fills the same box front-view/back-view do at 1x.
   */
  function naturalContainerRect(container) {
    const layers = container.closest('.murphy-door-configurator__image-layers');
    if (layers && layers.offsetWidth && layers.offsetHeight) {
      return { width: layers.offsetWidth, height: layers.offsetHeight };
    }
    return container.getBoundingClientRect();
  }

  /**
   * Which face is the gallery ACTUALLY showing?
   *
   * 🚨 READ FROM THE DOM, NEVER FROM OUR OWN STATE. This used to track a
   * `state.view` updated by a `configurator:view-changed` DOM event — an event
   * NOTHING IN THE THEME DISPATCHES. media-gallery.js's setView() flips inline
   * `visibility` and `aria-hidden` on the two containers and publishes nothing
   * of that name, so the listener was dead from the first commit and the value
   * never moved off 'front'.
   *
   * The failure was silent and total: after the customer flipped to Back, the
   * blueprint kept drawing into the hidden front container, so every chain,
   * leader and glyph rendered correctly into a `visibility: hidden` subtree and
   * the drawing simply never appeared. Same shape as the bespoke-analytics-event
   * footgun in CLAUDE.md — publishing or listening on a channel nobody else
   * speaks fails without an error.
   *
   * Visibility is the gallery's own authoritative signal, so reading it cannot
   * drift and needs no cooperation from media-gallery.js.
   */
  function currentView() {
    const back = viewContainer('back');
    if (!back) return 'front';

    // aria-hidden FIRST, computed visibility second. setView() flips both in
    // sync (the gallery snippet documents this: "flips both visibility and
    // aria-hidden in sync with the rotateY transform"), and the attribute is the
    // more robust read of the two — getComputedStyle().visibility returns
    // undefined under jsdom, so a visibility-only check is untestable and would
    // have to be trusted rather than proven.
    const hidden = back.getAttribute('aria-hidden');
    if (hidden === 'true') return 'front';
    if (hidden === 'false') return 'back';

    const vis = getComputedStyle(back).visibility;
    return vis === 'visible' ? 'back' : 'front';
  }

  /**
   * Whether provisional trim numbers reach the customer. ON by default since
   * 2026-09-04, by the product owner's explicit call: the render-derived values
   * are accepted as close enough to show now, and will be corrected when
   * engineering confirms them.
   *
   * 🚨 THIS IS A POLICY SWITCH, NOT A CLEANUP. The per-profile `provisional:
   * true` flags in md-config-blueprint-geometry.js stay exactly as they are, so
   * the documented workflow still holds: as engineering confirms each number,
   * replace it and clear THAT entry's flag. Do not bulk-clear them - they are
   * the record of what is still unverified.
   *
   * Since rev. 2026-09-25 evening every entablature style (Standard, Square,
   * Crown) has sheet-sourced in-swing and out-swing heights, so no profile
   * carries swingDependent any more, and overall height is SUPPRESSED (reason
   * 'swing-unknown') until the customer picks a handing, rather than quietly
   * defaulting to one swing. No provisional trim numbers remain: the BACK
   * Square/Crown entablature WIDTH (widthProvisional) was the last holdout
   * and was confirmed against sheet C15 rev. 2026-09-28.
   *
   * The front casing reveal pair is also asymmetric now (hinge 1.75in vs
   * non-hinge 1.5in). It needs NO handing gate: this module already mirrors
   * both the art and the leaders for a right-hand door, so the calculator
   * hands over hinge-then-non-hinge unconditionally. See the double-mirror
   * warning in md-config-bl-dimension-calculator.js before changing either.
   */
  function includeProvisional() {
    try {
      if (window.__mdBlueprintShowProvisional === false) return false;
      if (new URLSearchParams(window.location.search).get('mdBlueprintProvisional') === '0') {
        return false;
      }
      return true;
    } catch (_) {
      return true;
    }
  }

  function publish(eventName, payload) {
    if (!eventName || !CoreEventBus || typeof CoreEventBus.publish !== 'function') return;
    try {
      CoreEventBus.publish(eventName, payload);
    } catch (err) {
      isDev() && console.error('[BlueprintOverlay] publish failed', eventName, err);
    }
  }

  function subscribe(eventName, handler) {
    if (!eventName || !CoreEventBus || typeof CoreEventBus.subscribe !== 'function') return;
    try {
      const off = CoreEventBus.subscribe(eventName, handler);
      if (typeof off === 'function') cleanupHandlers.push(off);
    } catch (err) {
      isDev() && console.error('[BlueprintOverlay] subscribe failed', eventName, err);
    }
  }

  // ==========================================================================
  // Rendering
  // ==========================================================================

  function el(tag, attrs, text) {
    const node = document.createElementNS(SVG_NS, tag);
    Object.keys(attrs || {}).forEach((k) => node.setAttribute(k, attrs[k]));
    if (text != null) node.textContent = text;
    return node;
  }

  /**
   * Converts a desired on-screen pixel size into user units.
   *
   * 🚨 THE DIVISOR IS THE FITTED SIDE, NOT THE CONTAINER WIDTH. viewBox is
   * 0 0 2048 2048 with the default preserveAspectRatio (xMidYMid meet), so the
   * drawing scales to min(width, height) and is CENTRED in the element box.
   * Those two agree only while the container is square. Passing the width
   * understated the divisor on every non-square container and shrank all type:
   * measured on production at a 355x292 container, a label authored for 10px
   * rendered at 8.2px (1.21x error), and at a 766x235 window the error is 3.3x.
   * Callers pass fittedPx from render(); see deriveSafeBottomCanvas() for the
   * same aspect-ratio trap applied to the controls' clearance.
   */
  function scaleText(fittedPx, desiredPx) {
    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    const canvas = (geo && geo.CANVAS) || 2048;
    if (!fittedPx) return desiredPx;
    return (desiredPx * canvas) / fittedPx;
  }

  /**
   * The two stacked controls the birdseye plan must finish above, in px from
   * the container bottom. Mirrors assets/section-md-configurator.css:
   * .md-measure-toggle (bottom:0, min-height:44px) and .md-bp-frameswitch
   * (bottom:44px, min-height:44px). Change either there, change this.
   */
  const SWITCH_RESERVE_PX = 88;

  /**
   * A visual gap between the drawing and the control, in px.
   *
   * Without it the cap makes the plan finish EXACTLY at the switch's top edge,
   * and getBBox() excludes stroke -- so the rendered stroke still crosses the
   * boundary. Measured on staging at a 355x292 container: the 24px overlap
   * closed to 1px, not 0. 3px covers that overshoot at the
   * smallest phone box without eating more of the figure than it has to. This is the difference between "the maths is right"
   * and "the customer sees a gap".
   */
  const SWITCH_CLEARANCE_PX = 3;

  /**
   * The canvas y of the frame switch's top edge, derived from the RENDERED box.
   *
   * 🚨 A CLEARANCE CONSTANT CANNOT BE AUTHORED IN CANVAS UNITS. The controls sit
   * at a fixed px offset from the container bottom while the drawing scales to
   * the fitted side, so one authored number is only ever right for one aspect
   * ratio. BIRDSEYE.SAFE_BOTTOM was calibrated on a square 435x435 desktop
   * container (deriving 1633 there) and left the plan overlapping the switch by
   * a measured 24px once the same container was 355x292 on a 375px viewport.
   * Returns null when the box is unmeasurable so the caller keeps the authored
   * value rather than silently uncapping the figure.
   */
  function reserveTopCanvas(rect, fittedPx, geo, reservePx, clearancePx) {
    const canvas = (geo && geo.CANVAS) || 2048;
    if (!rect || !fittedPx || !rect.height) return null;
    // meet centres a fittedPx x fittedPx content box inside the element box.
    const contentTopPx = (rect.height - fittedPx) / 2;
    const topPx = rect.height - reservePx - clearancePx - contentTopPx;
    const y = (topPx * canvas) / fittedPx;
    return Math.max(0, Math.min(canvas, y));
  }

  function deriveSafeBottomCanvas(rect, fittedPx, geo) {
    return reserveTopCanvas(rect, fittedPx, geo, SWITCH_RESERVE_PX, SWITCH_CLEARANCE_PX);
  }

  /**
   * The gallery's OTHER two pieces of chrome, measured the same way and for the
   * same reason -- they overlay the ELEVATION, which the switch cap never
   * covered because it was only ever asked about the birdseye plan.
   *
   * ZOOM: .configurator-zoom-controls is bottom:12px right:12px with three 44px
   * buttons and two 4px gaps = 140px tall, so it reserves the bottom 152px of
   * the RIGHT edge. Reported live: the Overall width label block sat wedged
   * between Zoom-in and Reset, on top of the buttons.
   *
   * CHROME_ROW: the bottom control row (.md-measure-toggle and the Front/Back
   * switch) is 44px tall and the Front/Back pill sits CENTRED, so it lands
   * squarely on the overall-width dimension rule in the floor band. The
   * birdseye switch stacked above it is left-column only and the rule never
   * reaches that far left, so one row -- not SWITCH_RESERVE_PX's two -- is the
   * honest reserve here.
   *
   * 🪤 That reasoning is TRUE FOR THE RULE AND FALSE FOR THE TEXT. The left
   * gutter's text blocks DO reach that far left, so they clamp against
   * caps.leftTextTop (two rows) instead -- see deriveChromeCaps. Do not
   * "simplify" the two back into one; that merge is the 2026-09-22 defect
   * where every right-hand door hid its overall-width value under the
   * birdseye switch.
   *
   * Same failure mode as SWITCH_* above: authored in px because the controls
   * are px-anchored to the container bottom while the drawing scales to the
   * fitted side. Never re-express either as a canvas constant.
   */
  const ZOOM_RESERVE_PX = 152;
  const ZOOM_CLEARANCE_PX = 6;
  const CHROME_ROW_PX = 44;
  const CHROME_ROW_CLEARANCE_PX = 6;

  /**
   * The y the overall-width rule actually draws at -- authored, or lifted clear
   * of the bottom control row when that row reaches it.
   *
   * 🚨 ONE OWNER, because the rule and its leader dot MUST agree. They are drawn
   * by two different functions, and the moment they compute the cap separately
   * the dot floats off the line it is supposed to land on. That is exactly what
   * the customer reported ("the leader dot is not connecting to the span") --
   * the rule was behind the Front/Back pill, so the dot appeared to point at
   * nothing. `T` here is drawBraces' tick half-length: the ticks stand off the
   * rule, so it is the tick tips, not the rule, that have to clear the row.
   */
  function floorBandRuleY(geo, caps, U) {
    const authored = ((geo && geo.BRACE) || {}).OVERALL_W_Y;
    if (!Number.isFinite(authored)) return authored;
    if (!caps || !Number.isFinite(caps.chromeTop)) return authored;
    return Math.min(authored, caps.chromeTop - 7 * U);
  }

  /**
   * The y the entablature-width rule draws at, and the one its leader dot
   * MUST agree with -- same reason floorBandRuleY exists (two functions
   * computing the same cap independently is how a dot ends up floating off
   * the line it names).
   *
   * 🚨 THIS DID NOT EXIST BEFORE. Every other elevation dimension --
   * rough-opening, door-unit, overall-height, overall-width -- has an actual
   * drawn rule with tick marks the leader dot lands on; entablature-width
   * had a number and a dot floating at a fixed canvas point (620, 70) with
   * NOTHING drawn under it. Reported live as "not helpful": at y=70 the dot
   * sits well above even the tallest entablature style's own top edge
   * (crown's traced bbox.T is 92), so it was never on, or even near, the art
   * it was supposed to measure.
   *
   * ENTABLATURE_WIDTH_CLEARANCE positions the rule ABOVE the entablature's
   * actual top edge for the SELECTED style -- crown (T 92), square (T 152) or
   * standard (T 188) -- the same style-dependent lookup drawBraces already
   * uses for overall-height's brace, so this is not a new pattern, it is the
   * existing one applied to the one dimension it was missing from. Returns
   * null when there is no entablature trace to measure from (no Front/Back
   * Entablature selected yet), matching the "no rule, no floating dot"
   * contract the rest of the file already keeps -- the callout itself is
   * omitted upstream in that case.
   */
  const ENTABLATURE_WIDTH_CLEARANCE = 60;
  function entablatureWidthRuleY(geo, entKey) {
    const entTrace = entKey ? geo.traces[entKey] : null;
    if (!entTrace) return null;
    return entTrace.bbox.T - ENTABLATURE_WIDTH_CLEARANCE;
  }

  /**
   * The elevation's label gutters, live-widened for whatever casing/
   * entablature art is ACTUALLY drawn -- CORRECTED 2026-09-12.
   *
   * geo.GUTTER_L/R (558/1545) were tuned once, flush to the FRONT view's
   * standard casing (front-casing-standard bbox L584..R1440), and every
   * caller (drawBraces' hardcoded 'front-casing-standard' lookup, drawLabels'
   * colL/colR, and the two 'ro-to-casing-edge-*' dots) treated that single
   * pair as a universal constant regardless of which VIEW or STYLE was
   * actually on screen. The back casing trace is wider on both edges
   * (back-casing-standard bbox L552..R1496) -- 6 units, not 26, of clearance
   * from GUTTER_L=558 -- and the casing leg itself spans L552..632, so the
   * fixed gutter anchor sits INSIDE the drawn rail. Every left-side label
   * (Entablature width, Rough Opening, Door Unit, Rough Opening to Casing
   * Edge) rendered on top of it. Crown entablature widens things further on
   * both sides (back-entablature-crown bbox L472..R1576, past BOTH fixed
   * gutters). Reported live 2026-09-12: "all overlap with the casing
   * blueprint line" on back-view screenshots.
   *
   * The fix is not a second fixed pair (that just moves the same bug to the
   * next style/view combo that ships) -- it is measuring the trace that is
   * ACTUALLY selected for the ACTUAL view, and only ever widening outward
   * from the tuned default via Math.min/Math.max, never narrowing it. A
   * config with no casing/entablature selected yet (casTrace/entTrace both
   * null) falls straight back to the original tuned 558/1545, so early-state
   * rendering is byte-identical to before this fix.
   */
  const ELEVATION_GUTTER_CLEARANCE = 40;
  function elevationGutters(geo, casTrace, entTrace) {
    let L = geo.GUTTER_L;
    let R = geo.GUTTER_R;
    [casTrace, entTrace].forEach((t) => {
      if (!t) return;
      L = Math.min(L, t.bbox.L - ELEVATION_GUTTER_CLEARANCE);
      R = Math.max(R, t.bbox.R + ELEVATION_GUTTER_CLEARANCE);
    });
    return { L, R };
  }

  /**
   * The measured average-character-width-to-font-size ratio for this font,
   * read off a REAL render (not guessed) -- see tests/assets/
   * dimension-calculator.test.js's own TITLE_ADVANCE/VALUE_ADVANCE block for
   * the full derivation table. Both titles and values measured to the same
   * ~0.60 ratio, so one shared constant covers both here.
   *
   * 🚨 WHY THIS EXISTS AT RUNTIME, NOT JUST IN A TEST: the dimension-
   * calculator's own title-fit test only checks authored copy against the
   * STATIC geo.GUTTER_L/R -- it has no way to know elevationGutters() can
   * narrow that at RENDER TIME to clear a wide casing/entablature trace (see
   * elevationGutters' own doc comment above). Measured live: back view +
   * Crown entablature narrows the left gutter to 432 canvas units, and
   * "Rough opening" (13 chars) needs 432.25 units to clear at a 370px
   * container by this same formula -- a MARGIN OF 0.25 CANVAS UNITS, not a
   * safe one, and negative (clipped) at any container narrower than 370px or
   * any future title one character longer. minGutterUnitsForCallouts()
   * closes that gap by computing the REAL minimum straight from whatever is
   * actually rendering, so it self-adjusts if a title is ever lengthened
   * rather than silently reverting to today's razor-thin margin.
   */
  const CHAR_ADVANCE = 0.60;

  function widestLineChars(c) {
    return Array.isArray(c.titleLines) && c.titleLines.length
      ? Math.max(...c.titleLines.map((line) => line.length))
      : (c.title || '').length;
  }

  /**
   * The minimum safe colL/colR for the ELEVATION frame, given what is
   * ACTUALLY about to render into each gutter -- computed from `callouts`
   * directly, using the same `left` assignment (authored side, flipped by
   * `mirrorSides`) drawLabels' own per-callout loop uses below, so this can
   * never drift out of sync with what really lands where.
   *
   * Widest of the TITLE line and the VALUE label is used per callout,
   * because the value can need more room than its own title -- "38″ × 82″"
   * (9 chars at the larger VALUE font) needs more canvas units than "Rough
   * opening" (13 chars at the smaller TITLE font) once the font-size
   * difference is folded in. Sub-lines are shorter than either in every
   * callout that carries one and are not included.
   */
  function minGutterUnitsForCallouts(callouts, slots, mirrorSides, TITLE, VALUE) {
    let minLeft = 0;
    let minRight = 0;
    callouts.forEach((c) => {
      const slot = slots[c.id];
      if (!slot || (slot.frame || 'elevation') !== 'elevation') return;
      const isLeft = mirrorSides ? slot.side !== 'left' : slot.side === 'left';
      const titleUnits = widestLineChars(c) * CHAR_ADVANCE * TITLE;
      const valueUnits = c.label ? String(c.label).length * CHAR_ADVANCE * VALUE : 0;
      const units = Math.max(titleUnits, valueUnits);
      if (isLeft) minLeft = Math.max(minLeft, units);
      else minRight = Math.max(minRight, units);
    });
    return { minLeft, minRight };
  }

  /**
   * 🚨 chromeTop AND leftTextTop ARE DIFFERENT NUMBERS ON PURPOSE. Reserving one
   * row for both is the bug this pair exists to prevent.
   *
   * chromeTop reserves ONE row (44px) because its only consumer is
   * floorBandRuleY -- the overall-width DIMENSION RULE, which runs across the
   * middle of the floor band where the only chrome is the centred Front/Back
   * pill. The birdseye switch stacked above that row is left-column only and
   * the rule never reaches that far left, exactly as CHROME_ROW_PX documents.
   *
   * leftTextTop reserves TWO rows (88px, SWITCH_RESERVE_PX) because the LEFT
   * GUTTER'S TEXT BLOCKS do land in that far-left column -- squarely under the
   * birdseye switch. Measured on production 2026-09-22 across all eight
   * front/back x handing combinations: on every RIGHT-hand door the
   * 'overall-width' block (authored ay 1700) mirrored into the left gutter and
   * landed at y343..386 of a 435px blueprint, while .md-bp-frameswitch occupies
   * y347..391 -- the label 64-69% covered, the sub-line and THE VALUE ITSELF
   * 90-100% covered, with elementFromPoint returning the switch at each block's
   * centre. Left-hand doors were clear, which is why this survived: the defect
   * only exists in the mirrored half of the matrix.
   *
   * Reusing SWITCH_RESERVE_PX rather than authoring a fourth number keeps one
   * definition of "two stacked 44px control rows" -- if a third row is ever
   * added, both the birdseye plan cap and this one move together.
   */
  function deriveChromeCaps(rect, fittedPx, geo) {
    return {
      zoomTop: reserveTopCanvas(rect, fittedPx, geo, ZOOM_RESERVE_PX, ZOOM_CLEARANCE_PX),
      chromeTop: reserveTopCanvas(rect, fittedPx, geo, CHROME_ROW_PX, CHROME_ROW_CLEARANCE_PX),
      leftTextTop: reserveTopCanvas(rect, fittedPx, geo, SWITCH_RESERVE_PX, CHROME_ROW_CLEARANCE_PX),
    };
  }

  /**
   * The elevation's mirror axis for hand -- the R.O.'s own horizontal centre.
   * The rough opening the photo depicts is symmetric (verified: carcass-front,
   * carcass-back and the OPENING rect all centre on this exact value too), so
   * mirroring the whole elevation drawing around it is a no-op for the parts
   * that ARE symmetric and a genuine flip for the parts that are not.
   */
  function elevationMirrorAxis(geo) {
    const o = geo.OPENING;
    return (o.L + o.R) / 2;
  }

  /**
   * SVG transform that flips the elevation drawing left<->right around its own
   * centreline. `null` when no mirror is wanted -- `el()` calls setAttribute
   * unconditionally on every key it is given, so callers must only ADD the
   * `transform` key when this returns truthy, never pass the null through, or
   * every drawing grows a literal `transform="null"`.
   *
   * 🚨 GROUP-LEVEL scale(-1,1) IS ONLY SAFE ON PURE LINE ART. It correctly
   * mirrors a <path>/<rect>/<line>'s geometry, but it ALSO mirrors <text> --
   * flipping the glyphs themselves into unreadable mirror writing. Every group
   * this is applied to (drawSilhouettes, drawBraces) contains only path/rect/
   * line children for exactly this reason. drawLabels' text and leaders are
   * mirrored a different way: each dot's x is individually reflected through
   * this same axis (see the elevation branch of the dot resolution below),
   * which moves the leader's target without touching the glyphs it draws.
   */
  function mirrorTransform(geo, mirror) {
    if (!mirror) return null;
    return `translate(${2 * elevationMirrorAxis(geo)} 0) scale(-1 1)`;
  }

  /**
   * Line art for whatever the customer has actually selected.
   * Traces are placement-only; nothing here implies a measurement.
   *
   * `mirror` flips the whole group for a right-hand door. The base art (every
   * trace, and the traced casing/entablature bboxes trimProfiles reports) is
   * authored left-hand -- confirmed off actual CAD geometry (an Outswing
   * Bookcase 36x80 FBX and a Flush Mount 12in FBX): front casing sits closer
   * to the hinge side (more overlap there, per engineering), and that hinge
   * sat on the LEFT in both models. A right-hand door is the mirror image of
   * that, and until this shipped nothing here ever drew it as one -- the
   * elevation showed the identical (left-hand) drawing regardless of the
   * customer's actual Door Handing selection.
   */
  function drawSilhouettes(svg, context, view, mirror) {
    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    if (!geo) return;
    const traces = geo.traces || {};

    const keys = [];
    keys.push(view === 'back' ? 'carcass-back' : 'carcass-front');
    if (view === 'back') {
      if (context.backCasingKey) keys.push(context.backCasingKey);
      if (context.backEntablatureKey) keys.push(context.backEntablatureKey);
    } else {
      if (context.frontCasingKey) keys.push(context.frontCasingKey);
      if (context.frontEntablatureKey) keys.push(context.frontEntablatureKey);
    }

    const attrs = { class: 'md-bp__art' };
    const mt = mirrorTransform(geo, mirror);
    if (mt) attrs.transform = mt;
    const group = el('g', attrs);
    keys.forEach((key) => {
      const trace = traces[key];
      if (!trace || !trace.d) return;
      group.appendChild(
        el('path', { d: trace.d, class: 'md-bp__silhouette', 'fill-rule': 'evenodd' })
      );
    });

    // Handing, jamb depth, door depth and pass-through are drawn by
    // drawPlanDetail() from render(), not here. They need the calculator's plan
    // block rather than the raw context, and the plan must be drawn AFTER the
    // braces so its anchor points are available to the leaders.

    // The rough opening itself, always drawn - it is the datum every vertical
    // dimension is measured from. Symmetric about the mirror axis by
    // definition, so mirroring it draws exactly the same rectangle.
    const o = geo.OPENING;
    group.appendChild(
      el('rect', {
        x: o.L, y: o.T, width: o.R - o.L, height: o.B - o.T,
        class: 'md-bp__opening',
      })
    );
    svg.appendChild(group);
  }

  /**
   * Plan detail: handing, jamb depth, door depth and pass-through in one figure.
   *
   * None of those four can be drawn in a front elevation -- which is exactly why
   * the configurator ships no layer art for any of the five door depths or
   * either jamb depth. The plan is the only view where they are real.
   *
   * 🚨 THE PIVOT IS NOT A BUTT HINGE. Murphy Door's patented pivot sits at the
   * jamb's BACK corner (hx, WALL_T) -- the same point in space a butt hinge
   * would occupy -- but attaches to the door along its SIDE edge, set in from
   * the door's front face by exactly the jamb depth, so a shut door lands
   * front-flush with the jamb. Rotating about a point set in from the face
   * makes the open leaf STRADDLE the jamb line, running
   * hx-(depth-jamb) .. hx+jamb rather than sitting wholly on one side of it,
   * and puts the sweep radius at hypot(doorWidth, jamb) rather than the door
   * width alone. Drawing it as a corner pivot is wrong hardware, and looks it.
   *
   * Returns the anchor points the CALLOUT_SLOTS dotFrom names resolve against,
   * because unlike every fixed span on the sheet these three move with the
   * selection -- a 12in door draws a visibly thicker leaf than a 5-1/4in one.
   */
  function drawPlanDetail(svg, plan, containerWidthPx, layout, outlineOnly) {
    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    if (!geo || !plan || !Number.isFinite(plan.doorWidthIn) || !Number.isFinite(plan.doorDepthIn)) {
      return null;
    }
    const P = geo.PLAN;
    const U = scaleText(containerWidthPx, 1);

    /* Scale to FIT, capped at P.SCALE.
       The plan is centred on the door it sits inside, and must clear its edges
       on both sides including the wall returns. At the full 13 u/in a 49in door
       needs 2*halfW + 2*RET = 847 units against a 792-unit door interior, so
       the widest doors scale down rather than running off the drawing. */
    /* The LAYOUT BOX, not the drawing, is what changed when the plan moved into
       its own frame. Default is the historical one -- centred in the door and
       fenced by its edges -- so the elevation path is bit-for-bit unchanged;
       the birdseye frame simply passes a bigger box. */
    const car = geo.traces['carcass-front'].bbox;
    const elevCX = (car.L + car.R) / 2;
    const L = layout || {
      CX: elevCX,
      HALF_AVAIL: Math.min(elevCX - car.L, car.R - elevCX) - 12 - L.RET,
      BAND_TOP: L.BAND_TOP,
      BAND_BOTTOM: L.BAND_BOTTOM,
      RET: L.RET,
      SCALE: P.SCALE,
    };
    const planCX = L.CX;
    /* Capped three ways: the declared cap, the width between the returns, and
       the height of the band. The band term is new and is inert for the
       elevation (940 units is never the binding constraint at 13 u/in) -- it
       exists so a 49in door cannot outgrow the taller birdseye frame. */
    /* Capped four ways: the declared cap, the width between the returns, the
       height of the band, and -- when the layout declares one -- a SAFE_BOTTOM
       the figure and its caption must finish above.

       🚨 THE FOURTH CAP EXISTS BECAUSE THE SLIDE HAS CONTROLS ON IT. In-swing
       puts the wall at BAND_TOP + leafLen, so a wide door pushes the whole
       figure down: measured, a 49in door ran its wall to y1718 and its caption
       to y1916, through the frame switch (y1615..1841) and the Front/Back row
       (y1841..2048). The jamb and the caption are both included in the budget
       because both are drawn BELOW the wall line. */
    const capRoom = 42 * U + 12;          // caption block, in canvas units
    const jambPerIn = Number.isFinite(plan.jambIn) ? plan.jambIn : 4.5625;
    const safeCap = Number.isFinite(L.SAFE_BOTTOM)
      ? (L.SAFE_BOTTOM - L.BAND_TOP - capRoom) / (plan.doorWidthIn + jambPerIn)
      : Infinity;
    const scale = Math.min(
      L.SCALE,
      (2 * L.HALF_AVAIL) / plan.doorWidthIn,
      (L.BAND_BOTTOM - L.BAND_TOP) / plan.doorWidthIn,
      safeCap
    );
    const halfW = (plan.doorWidthIn * scale) / 2;
    // A wall must have thickness even when the customer picked the Custom jamb
    // (which parses to null). Draw the band at the standard 4-9/16in so the
    // figure still reads; the jamb CALLOUT is separately suppressed or says
    // "Custom", so no invented number reaches the customer as a dimension.
    const jambIn = Number.isFinite(plan.jambIn) ? plan.jambIn : 4.5625;
    const jamb = jambIn * scale;
    const depth = plan.doorDepthIn * scale;

    /**
     * Left-hand in-swing is authored; the other three are mirrors of it.
     * Mirroring rather than re-deriving keeps one geometry to be right about.
     * Out-swing is a mirror ASSUMPTION -- engineering has not confirmed whether
     * the pivot sits identically on an out-swing unit, and CLAUDE.md records
     * that out-swing also permanently attaches a casing piece to the door face.
     */
    /* Swing is OPTIONAL. With no handing chosen there is no swing to draw, so
       the door is drawn SHUT: wall, leaf lying in the opening front-flush with
       the jamb, both depths dimensionable, and no arc, pivot or caption
       inventing a direction nobody picked. Depth and jamb are real the moment
       they are selected, and they used to go unshown until handing arrived --
       measured on the live page, picking Door Depth drew nothing at all. */
    const known = !!(plan.hand && plan.swing);
    const mirrorX = plan.hand === 'Right';
    const mirrorY = known && plan.swing !== 'Inswing';

    /* 🚨 THE WALL'S Y IS COMPUTED, NOT FIXED.
       In-swing the leaf rises from the wall, so the wall sits LOW in the band.
       Out-swing it falls, so the wall sits HIGH. A single fixed value works for
       one and breaks the other: measured on the live dev theme, a 38in
       out-swing leaf ran from y1619 to y2113 against a 2048 canvas -- 157 units
       off the bottom of the drawing and straight through the Front/Back switch.
       BAND_BOTTOM stops short of the carcass floor to keep the caption clear of
       that switch. */
    const leafLen = 2 * halfW;
    const capH = 52 * U;
    const wallT = !known
      ? Math.round((L.BAND_TOP + L.BAND_BOTTOM) / 2)
      : (mirrorY
          ? L.BAND_TOP
          : Math.max(L.BAND_TOP + leafLen, L.BAND_BOTTOM - capH - jamb));
    const wallB = wallT + jamb;
    const midY = wallT + jamb / 2;
    const mx = (x) => (mirrorX ? 2 * planCX - x : x);
    const my = (y) => (mirrorY ? 2 * midY - y : y);

    const hx = planCX - halfW;
    const fx = planCX + halfW;
    /* 🚨 OUT-SWING MEETS THE HINGE ON THE OPPOSITE FACE.
       The hinge is fixed against the jamb and does not move with the swing --
       what inverts is which face of the door is against it, because the door is
       hung from the other side. In-swing the leaf sits `depth - jamb` back from
       the hinge, so the hinge is `jamb` from its FRONT face; out-swing the leaf
       shifts to sit `jamb` back, putting the hinge `jamb` from the other face.
       Either way the hinge ends up `jamb` from the leaf's NEAR face, which is
       the invariant the test asserts.
       Getting this wrong drew the out-swing door hung backwards -- and moving
       the PIVOT instead of the leaf, as a first attempt did, is not the same
       thing: it detaches the hinge from the jamb it is supposed to be fixed to. */
    const leafL = hx - (mirrorY ? jamb : depth - jamb);
    const leafTop = wallT - leafLen;
    const R = Math.hypot(leafLen, jamb);
    // The span crosses the doorway just inside the wall on the swing side, so
    // it travels with the wall rather than sitting at a fixed canvas y.
    const passY = wallT - L.RET;

    const g = el('g', { class: 'md-bp__plan' });
    const line = (x1, y1, x2, y2, cls) =>
      g.appendChild(el('line', { x1: mx(x1), y1: my(y1), x2: mx(x2), y2: my(y2), class: cls }));

    // --- wall, cut ---------------------------------------------------------
    // Hatched, because RET/RET_L are short enough that plain outlines read as
    // two floating boxes rather than a wall the door is cut into. The left
    // return runs longer so the jamb leader has landing room clear of the pivot.
    /* The returns run to L.WALL_L/R when the layout supplies them -- the
       birdseye frame runs the wall its full width, which reads as a wall the
       door is cut INTO rather than two floating stubs. Falls back to RET so
       the in-elevation layout is unchanged. */
    const wallL = Number.isFinite(L.WALL_L) ? L.WALL_L : hx - L.RET;
    const wallR = Number.isFinite(L.WALL_R) ? L.WALL_R : fx + L.RET;
    [[wallL, hx], [fx, wallR]].forEach(([a, b]) => {
      const x = Math.min(mx(a), mx(b));
      g.appendChild(el('rect', {
        x, y: Math.min(my(wallT), my(wallB)), width: Math.abs(b - a), height: jamb,
        class: 'md-bp__plan-wall',
      }));
      for (let t = a - jamb + 14; t < b; t += 18) {
        const x1 = Math.max(t, a);
        const x2 = Math.min(t + jamb, b);
        if (x2 > x1) line(x1, wallT + (x1 - t), x2, wallT + (x2 - t), 'md-bp__plan-hatch');
      }
      /* Name the hatch. Only where the return can actually hold the word.
         🚨 THE GATE IS DERIVED FROM THE TEXT, NOT A MAGIC MULTIPLE. It was
         `> 26 * U * 4` (490 units at a 435px container), calibrated when the
         returns ran nearly the full canvas. Pulling the wall in to clear the
         zoom column (WALL_L/R 60/1988 -> 308/1740) halved them to 285 units, so
         every label silently vanished -- caught on staging, not by any test,
         because the clearance work only checked geometry bounds.
         Advance estimate: ~0.62em per glyph for this mono face, plus the
         letter-spacing between them, plus 15% breathing room. */
      const _fs = 9.5 * U;
      const _ls = 2.2 * U;
      const _need =
        (L.WALL_LABEL.length * 0.62 * _fs + (L.WALL_LABEL.length - 1) * _ls) * 1.15;
      if (!outlineOnly && L.WALL_LABEL && Math.abs(b - a) > _need) {
        g.appendChild(el('text', {
          x: mx((a + b) / 2), y: my(wallT + jamb / 2) + 3.4 * U,
          'text-anchor': 'middle', 'font-size': 9.5 * U, 'letter-spacing': 2.2 * U,
          class: 'md-bp__plan-wall-label',
        }, L.WALL_LABEL));
      }
    });

    // --- leaf: its THICKNESS is the door depth ------------------------------
    // Open, it stands perpendicular to the wall (depth along x). Shut, it lies
    // across the opening (width along x, depth along y). Either way its
    // thickness IS the depth, which is why the plan carries that dimension.
    const shutY = wallB - depth;
    const leafAttrs = known
      ? {
          x: Math.min(mx(leafL), mx(leafL + depth)),
          y: Math.min(my(leafTop), my(wallT)),
          width: depth, height: leafLen, class: 'md-bp__plan-leaf',
        }
      : {
          x: Math.min(mx(hx), mx(fx)), y: shutY,
          width: leafLen, height: depth, class: 'md-bp__plan-leaf',
        };
    g.appendChild(el('rect', leafAttrs));

    /* Name the leaf, exactly as the wall band is named -- same face, same size,
       same letter-spacing, same derived gate.

       🚨 ORIENTATION IS READ FROM THE DRAWN RECT, NOT FROM `known`. The leaf is
       tall-and-thin when the door is open and long-and-flat when it is shut, and
       all four handings mirror through mx()/my() -- so deciding rotation from
       the model would have to re-derive the mirroring and could drift from the
       rect. Measuring the attrs we just drew cannot.

       TWO gates, because a door has a dimension a wall band does not: the word
       must fit ALONG the leaf, and the leaf must be thick enough ACROSS to hold
       the glyphs. Thickness IS the door depth, so the shallowest door on the
       smallest container is the binding case. */
    if (L.DOOR_LABEL) {
      const _dfs = 9.5 * U;
      const _dls = 2.2 * U;
      const _dneed =
        (L.DOOR_LABEL.length * 0.62 * _dfs + (L.DOOR_LABEL.length - 1) * _dls) * 1.15;
      const along = Math.max(leafAttrs.width, leafAttrs.height);
      const across = Math.min(leafAttrs.width, leafAttrs.height);
      const cx = leafAttrs.x + leafAttrs.width / 2;
      const cy = leafAttrs.y + leafAttrs.height / 2;
      if (!outlineOnly && along > _dneed && across > _dfs * 1.15) {
        const attrs = {
          x: cx, y: cy + 3.4 * U,
          'text-anchor': 'middle', 'font-size': _dfs, 'letter-spacing': _dls,
          class: 'md-bp__plan-door-label',
        };
        // Rotate about the leaf's CENTRE, not the baseline point: the 3.4*U
        // optical offset then stays perpendicular to the text run, which is
        // what centres the glyphs across the thickness in both orientations.
        if (leafAttrs.height > leafAttrs.width) {
          attrs.transform = `rotate(-90 ${cx} ${cy})`;
        }
        g.appendChild(el('text', attrs, L.DOOR_LABEL));
      }
    }

    if (known) {
      // --- sweep of the door's front free corner ------------------------------
      // Mirroring reverses orientation, so the arc's sweep flag flips once per
      // mirror; two mirrors return it to the original.
      const sweep = (mirrorX !== mirrorY) ? 0 : 1;
      // a0 is the leaf's front free corner, which follows the leaf's own shift.
      const a0 = { x: mx(hx + (mirrorY ? -jamb : jamb)), y: my(leafTop) };
      const a1 = { x: mx(fx), y: my(wallB) };
      g.appendChild(el('path', {
        d: `M ${a0.x} ${a0.y} A ${R} ${R} 0 0 ${sweep} ${a1.x} ${a1.y}`,
        class: 'md-bp__plan-arc', fill: 'none',
      }));

      // Travel-direction arrowhead, tangent to the sweep, near the open end.
      const th0 = Math.atan2(wallT - leafTop, jamb);
      const th1 = Math.atan2(wallT - wallB, 2 * halfW);
      const arcPt = (t) => {
        const a = th1 + (th0 - th1) * t;
        return { x: mx(hx + R * Math.cos(a)), y: my(wallT - R * Math.sin(a)) };
      };
      const A = arcPt(0.82);
      const B = arcPt(0.84);
      const len = Math.hypot(B.x - A.x, B.y - A.y) || 1;
      const ux = (B.x - A.x) / len;
      const uy = (B.y - A.y) / len;
      const TIP = 42;
      const HALF = 17;
      g.appendChild(el('polygon', {
        class: 'md-bp__plan-arrow',
        points:
          `${A.x + ux * TIP},${A.y + uy * TIP} ` +
          `${A.x + uy * HALF},${A.y - ux * HALF} ` +
          `${A.x - uy * HALF},${A.y + ux * HALF}`,
      }));

      // The hinge is fixed against the jamb -- it does NOT move with the swing.
      // What inverts out-swing is which FACE of the leaf meets it; see leafL.
      g.appendChild(el('circle', { cx: mx(hx), cy: my(wallT), r: 15, class: 'md-bp__plan-pivot' }));
    }

    // --- pass-through span --------------------------------------------------
    // Only drawn when the space calculator produced a number. Dotted rather
    // than dashed so it does not read as another elevation rule.
    // Traveling Preview chip: this is a measurement indicator, not outline
    // geometry -- skip it same as every drawLabels() callout, even though it
    // is drawn here rather than there.
    let passAnchor = null;
    if (!outlineOnly && Number.isFinite(plan.passThroughIn)) {
      const pl = hx + jamb;
      const T = 7 * U;
      line(pl, passY, fx, passY, 'md-bp__plan-pass');
      [pl, fx].forEach((x) => line(x, passY - T, x, passY + T, 'md-bp__plan-pass-tick'));
      passAnchor = { x: mx((pl + fx) / 2), y: my(passY) };
    }

    // --- handing caption ----------------------------------------------------
    // Anchored to the bottom of the whole FIGURE, not to the wall: under the
    // out-swing mirror the wall is the TOP of the figure, so anchoring there
    // dropped the caption on top of the leaf.
    if (!outlineOnly && known) {
      const figureBottom = Math.max(
        Math.min(my(leafTop), my(wallT)) + leafLen,
        Math.min(my(wallT), my(wallB)) + jamb
      );
      const title = el('text', {
        x: mx(planCX), y: figureBottom + 26 * U, class: 'md-bp__label',
        'text-anchor': 'middle', 'font-size': 10 * U,
      }, 'Door handing');
      const value = el('text', {
        x: mx(planCX), y: figureBottom + 42 * U, class: 'md-bp__value',
        'text-anchor': 'middle', 'font-size': 11.5 * U,
      }, `${plan.hand}, ${plan.swing === 'Inswing' ? 'in-swing' : 'out-swing'}`);
      g.appendChild(title);
      g.appendChild(value);
    }

    svg.appendChild(g);

    return {
      // The depth is spanned by whichever of the leaf's SHORT edges is
      // available: its top edge when the door is drawn open, its left edge when
      // drawn shut. The long edges span the door's WIDTH, so a dot there would
      // name the wrong dimension entirely.
      'plan:leaf-top': known
        ? { x: mx(leafL + depth / 2), y: my(leafTop) }
        : { x: mx(hx), y: shutY + depth / 2 },
      /* ON the jamb FACE, centred across its thickness -- the vertical line the
         opening is measured between, which is the surface a tape actually reads.
         It sat on the band's CORNER (hx-12, wallT), which names a point rather
         than the face and made the leader look like it was pointing at the end
         of the wall. Mirroring carries it to the correct band and the correct
         face automatically: mx(hx) is the right-hand edge of the left return,
         and on a right-hand door the same expression is the LEFT-hand edge of
         the right return. */
      'plan:left-return': { x: mx(hx), y: my(wallT + jamb / 2) },
      'plan:pass-span': passAnchor,
    };
  }

  /**
   * The four elevation braces.
   *
   * 🚨 EVERY SPAN IS A FIXED CANVAS COORDINATE. The first renderer scaled each
   * span by valueIn / BASE_RENDER_RO, so a brace only lined up on the base 38x82
   * render and a 48in door drew one wider than the door it measured. The artwork
   * does not change with the selection, so neither may the spans -- only the
   * numbers do.
   *
   * R.O. and door are nested L-corner braces at the top-left rather than four
   * separate chains: they are the same measurement stated twice, always exactly
   * 2in apart, and nesting them one step apart is the standard drafting read for
   * that while leaving the whole lower door free for the plan detail.
   *
   * `entKey` NAMES A TRACE, and is the one thing here that varies with the
   * selection. That is not a hole in the rule above: the overall-height brace
   * caps at the top of the ENTABLATURE, which is artwork, and the artwork
   * genuinely changes when the customer picks a different one -- the traced
   * tops run y92 (Crown), y152 (Square), y188 (Standard), a 96-unit spread
   * that hardcoding Crown floated 20px clear of the moulding it was measuring.
   * A trace name is still art. What must never arrive here is a VALUE.
   *
   * `mirror` flips the whole group for a right-hand door -- see the identical
   * parameter on drawSilhouettes for why (this group is pure line/tick art, no
   * text, so the same group-level scale(-1,1) is safe here too).
   *
   * `casKey` NAMES A TRACE, same contract as `entKey` -- CORRECTED 2026-09-12.
   * This used to hardcode 'front-casing-standard' regardless of which view or
   * casing style was actually on screen, so the 'overall width' rule below
   * (casing edge to casing edge) was drawn at the FRONT casing's width
   * (L584..R1440) even while viewing the BACK, whose casing legs are traced
   * wider (L552..R1496) and sit at entirely different x's. Reported live:
   * "Span line for overall width casing to casing is no longer aligned
   * properly" on back-view screenshots. Falls back to front-casing-standard
   * when the caller has no resolved key yet (no casing selected), matching
   * the prior always-front-standard behaviour for that early state.
   */
  function drawBraces(svg, containerWidthPx, entKey, caps, mirror, casKey) {
    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    if (!geo) return;
    const o = geo.OPENING;
    const b = geo.BRACE;
    const car = geo.traces['carcass-front'].bbox;
    const casTrace = (casKey && geo.traces[casKey]) || geo.traces['front-casing-standard'];
    const cas = casTrace.bbox;
    const U = scaleText(containerWidthPx, 1);
    const T = 7 * U;

    const gAttrs = { class: 'md-bp__dims' };
    const mt = mirrorTransform(geo, mirror);
    if (mt) gAttrs.transform = mt;
    const g = el('g', gAttrs);
    const rule = (x1, y1, x2, y2) =>
      g.appendChild(el('line', { x1, y1, x2, y2, class: 'md-bp__rule' }));
    const tick = (x1, y1, x2, y2) =>
      g.appendChild(el('line', { x1, y1, x2, y2, class: 'md-bp__tick' }));

    // rough opening: L-corner on the opening's top-left
    rule(o.L, b.RO_H_Y, o.R, b.RO_H_Y);
    tick(o.L, b.RO_H_Y - T, o.L, b.RO_H_Y + T);
    tick(o.R, b.RO_H_Y - T, o.R, b.RO_H_Y + T);
    rule(b.RO_V_X, o.T, b.RO_V_X, o.B);
    tick(b.RO_V_X - T, o.T, b.RO_V_X + T, o.T);
    tick(b.RO_V_X - T, o.B, b.RO_V_X + T, o.B);

    // door unit: nested one step inside
    rule(car.L, b.DOOR_H_Y, car.R - 4, b.DOOR_H_Y);
    tick(car.L, b.DOOR_H_Y - T, car.L, b.DOOR_H_Y + T);
    tick(car.R - 4, b.DOOR_H_Y - T, car.R - 4, b.DOOR_H_Y + T);
    rule(b.DOOR_V_X, car.T, b.DOOR_V_X, car.B - 4);
    tick(b.DOOR_V_X - T, car.T, b.DOOR_V_X + T, car.T);
    tick(b.DOOR_V_X - T, car.B - 4, b.DOOR_V_X + T, car.B - 4);

    /* overall width: casing edge to casing edge.
       🚨 IT CANNOT ALWAYS LIVE IN THE FLOOR BAND. The gallery's bottom control
       row overlays that band and the Front/Back pill sits CENTRED in it, so the
       authored y=1994 rule ran straight behind the pill -- reported live. Pull
       it up above that row when the row reaches it, ticks included.

       Above the floor line is a legitimate home for it, not a compromise: the
       rough-opening and door-unit rules already sit INSIDE the drawing
       (RO_H_Y = OPENING.T + 52), so a dimension over the art is the house
       style here, and the witness lines still tie it to the casing. */
    const cl = cas.L;
    const cr = cas.R - 4;
    const wY = floorBandRuleY(geo, caps, U);
    rule(cl, wY, cr, wY);
    tick(cl, wY - T, cl, wY + T);
    tick(cr, wY - T, cr, wY + T);
    // The witness line runs from the floor to just PAST the rule, so its
    // overshoot flips with the rule: below the floor it reaches down, above it
    // reaches up. Signing it off the rule's own side keeps it from doubling
    // back through the dimension it is anchoring.
    const away = wY > o.B ? 1 : -1;
    /* 🚨 THE WITNESS LINE IS REDUNDANT INK WHEN THE RULE IS LIFTED, AND IT IS
       LIFTED IN THE COMMON CASE. Below the floor (away=1) it crosses empty
       canvas and genuinely ties the rule to the casing. Above the floor
       (away=-1, whenever the bottom control row reaches the band -- i.e. every
       desktop 435px container) it runs at x=cl/cr, which is exactly where the
       casing leg is ALREADY drawn floor-to-head. Two strokes, same x, same
       span.

       Measured on production 2026-09-22, clustering every vertical edge within
       one inch: x=584 carried 2 edges at spread 0.00in (silhouette + ext), and
       the latch side carried 4 edges inside 0.95in with two of them exactly
       coincident at x=1436. Drafting convention agrees with the measurement --
       you do not draw a witness line on top of an object line; the object line
       serves as its own witness.

       So: draw it only when it has empty canvas to cross. The remaining
       1416/1425/1436 trio is NOT noise -- those are three real features
       (carcass edge, opening edge, casing edge) that genuinely sit within an
       inch of each other, and collapsing them would be a lie about the door. */
    if (away === 1) {
      [cl, cr].forEach((x) =>
        g.appendChild(el('line', { x1: x, y1: o.B + away, x2: x, y2: wY + T * away, class: 'md-bp__ext' }))
      );
    }

    // overall height: right wall lane, the SELECTED entablature's top to floor.
    // Drawn only when there is an entablature -- its number is suppressed
    // without one, and a brace with no number points at nothing.
    const entTrace = entKey ? geo.traces[entKey] : null;
    if (entTrace) {
      const entTop = entTrace.bbox.T;
      rule(b.OVERALL_H_X, entTop, b.OVERALL_H_X, o.B);
      tick(b.OVERALL_H_X - T, entTop, b.OVERALL_H_X + T, entTop);
      tick(b.OVERALL_H_X - T, o.B, b.OVERALL_H_X + T, o.B);
      g.appendChild(el('line', { x1: cr, y1: o.B, x2: b.OVERALL_H_X + T, y2: o.B, class: 'md-bp__ext' }));

      // entablature width: outer edge to outer edge of the SELECTED
      // entablature trace, above its own top edge -- see
      // entablatureWidthRuleY's doc comment for why this rule did not exist
      // before and what it fixes. Witness lines run from the entablature's
      // actual top edge UP to just past the rule, matching the overall-width
      // rule's away-sign convention above (here the rule is always above the
      // art it measures, so away is always -1: the witnesses reach up).
      const ew = entTrace.bbox;
      const ewY = entablatureWidthRuleY(geo, entKey);
      rule(ew.L, ewY, ew.R, ewY);
      tick(ew.L, ewY - T, ew.L, ewY + T);
      tick(ew.R, ewY - T, ew.R, ewY + T);
      [ew.L, ew.R].forEach((x) =>
        g.appendChild(el('line', { x1: x, y1: entTop, x2: x, y2: ewY - T, class: 'md-bp__ext' }))
      );
    }

    svg.appendChild(g);
  }

  /**
   * Callout blocks and their leaders.
   *
   * Leaders are angled with a short horizontal shoulder off the text, which is
   * ordinary drafting form. The shoulder is PROPORTIONAL to the run: a fixed one
   * ate most of the horizontal travel on the short leaders -- overall height has
   * only ~79 units of margin between its column and its brace -- and left them
   * reading as hooks rather than angled lines.
   */
  // ==========================================================================
  // Frames
  //
  // The blueprint is TWO drawings, not one. The elevation carries the four
  // authoritative dimensions; the birdseye carries the three that only exist in
  // plan -- door depth, jamb depth and pass-through -- plus the handing figure.
  //
  // They were one drawing until the plan detail outgrew the space between the
  // door's edges. Splitting them is not decluttering: it is what lets the plan
  // roughly double in scale, which is the only reason the shallowest door's leaf
  // clears its 12px legibility floor on a phone.
  //
  // 🚨 NOTHING SWITCHES THE FRAME ON A TIMER. Two designs have now been tried
  // and removed: a continuous cross-fade, and a short four-step demo on entry
  // that alternated the frames to teach the control. Both read well in
  // isolation. Both are wrong here, for a reason that has nothing to do with
  // WCAG 2.2.2 (which the short one did technically clear, at 4.6s): these are
  // numbers customers TRANSCRIBE onto a notepad with a tape measure in hand,
  // and content that moves out from under a reader mid-copy is hostile.
  //
  // The frame changes for exactly two reasons, both of them a customer asking:
  // the switch is worked by hand, or the category they just opened only exists
  // in one of the two drawings -- see selectFrameForCategory.
  // ==========================================================================

  function makeFrameSvg(geo, frame) {
    return el('svg', {
      class: `md-bp md-bp--${frame}`,
      viewBox: `0 0 ${geo.CANVAS} ${geo.CANVAS}`,
      xmlns: SVG_NS,
      'aria-hidden': 'true',
      focusable: 'false',
      preserveAspectRatio: 'xMidYMid meet',
    });
  }

  /** The birdseye's layout box -- the frame, rather than the door, is the fence. */
  function birdseyeLayout(geo, derivedSafeBottom) {
    const B = geo.BIRDSEYE;
    return {
      CX: B.CX,
      HALF_AVAIL: (B.GUTTER_R - B.GUTTER_L) / 2 - B.RET,
      BAND_TOP: B.BAND_TOP,
      BAND_BOTTOM: B.BAND_BOTTOM,
      RET: B.RET,
      SCALE: B.SCALE,
      WALL_L: B.WALL_L,
      WALL_R: B.WALL_R,
      WALL_LABEL: B.WALL_LABEL,
      DOOR_LABEL: B.DOOR_LABEL,
      /* Never LOOSER than the reviewed constant -- the derived value only
         ever tightens the figure on a non-square container. */
      SAFE_BOTTOM: Number.isFinite(derivedSafeBottom)
        ? Math.min(B.SAFE_BOTTOM, derivedSafeBottom)
        : B.SAFE_BOTTOM,
    };
  }

  /**
   * Border only. The ELEVATION deliberately gets NEITHER: it is the frame the
   * customer already knows, and adding chrome to it would change a drawing
   * that is signed off. The switch labels carry the naming for both -- the
   * birdseye used to repeat its own name on-canvas too ("BIRDSEYE VIEW" plus
   * an underline rule), removed 2026-09-12 as redundant with the switch.
   */
  function drawBirdseyeChrome(svg, geo) {
    const B = geo.BIRDSEYE;
    const i = B.FRAME_INSET;
    svg.appendChild(el('rect', {
      x: i, y: i, width: geo.CANVAS - 2 * i, height: geo.CANVAS - 2 * i,
      rx: 8, class: 'md-bp__frame',
    }));
  }

  /**
   * The drawings are aria-hidden -- an SVG dimension line tells a screen reader
   * nothing. Nothing subscribes to UI_BLUEPRINT_CALLOUTS_RENDERED, so before
   * this the numbers reached no text surface at all. Each frame carries its own
   * summary, and only the visible frame's is exposed, which is also what gives
   * the switch something to announce.
   */
  function frameSummary(callouts, slots, frame, label, note) {
    const mine = callouts.filter((c) => ((slots[c.id] || {}).frame || 'elevation') === frame);
    if (!mine.length) return null;
    const node = document.createElement('p');
    node.className = 'md-bp__sr';
    /* `note` carries anything about the DRAWING rather than the numbers -- today
       only the example-art disclaimer. It has to be said here because the
       on-canvas caption lives inside an aria-hidden <svg>, so a screen-reader
       user would otherwise hear the dimensions with no hint that the door being
       described is illustrative. */
    /* The extra pair is spoken as its own sentence, not appended to the one
       above it -- "Door depth: 12 inches. Shelf depth: 10 1/4 inches." Sharing
       a block is a LAYOUT fact; to a screen reader they are simply two
       dimensions, and running them together would imply one qualifies the
       other. This is the only path a screen reader has to these numbers (the
       drawing is aria-hidden), so an extra omitted here is inaudible. */
    node.textContent = `${label}. ` +
      (note ? `${note} ` : '') +
      mine.map((c) => `${c.title}${c.sub ? ` ${c.sub}` : ''}: ${c.label}.`
        + (c.extra && c.extra.label ? ` ${c.extra.title}: ${c.extra.label}.` : ''))
        .join(' ');
    return node;
  }

  /** One frame: the drawing plus its own screen-reader summary. */
  function frameShell(name, svg, summary) {
    const shell = document.createElement('div');
    shell.className = 'md-bp-frame';
    shell.setAttribute('data-frame', name);
    shell.appendChild(svg);
    if (summary) shell.appendChild(summary);
    return shell;
  }

  function showFrame(name) {
    const wrap = _framesEl;
    if (!wrap) return;
    const has = wrap.querySelector('.md-bp-frame[data-frame="birdseye"]');
    const target = has ? name : 'elevation';
    // Captured BEFORE state.frame is overwritten -- render() re-asserts the
    // CURRENT frame on every redraw ("showFrame(state.frame)"), and that call
    // must never fire the event below. Only a genuine elevation<->birdseye
    // transition (the manual switch, or selectFrameForCategory's already-guarded
    // auto-select) should reset zoom; a same-frame re-render is not a transition
    // the customer experienced.
    const changed = state.frame !== target;
    state.frame = target;
    wrap.querySelectorAll('.md-bp-frame').forEach((f) => {
      const on = f.getAttribute('data-frame') === target;
      f.classList.toggle('is-active', on);
      f.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    const btn = _frameSwitch || document.querySelector('.md-bp-frameswitch');
    if (btn) {
      const on = target === 'birdseye';
      // aria-checked carries the state; the label stays STABLE, same rule the
      // measurement toggle and the Front/Back switch follow.
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      const slider = btn.querySelector('.toggle-slider');
      if (slider) slider.classList.toggle('active', on);
    }
    setFrontBackVisible(target !== 'birdseye');
    /**
     * media-gallery.js's ZoomController already resets zoom on Front/Back
     * ('view_toggle') and slide change -- the elevation<->birdseye switch is
     * the same class of event and was the one case left un-reset. Without
     * this, a customer zoomed into a shelf detail who then picks a swing
     * direction (auto-switching to birdseye) lands on the plan drawing still
     * zoomed/panned to an unrelated corner of a DIFFERENT drawing.
     *
     * Published, not called directly: media-gallery.js owns the ZoomController
     * instance and this module has no reference to it (CCP -- no peer-to-peer
     * module calls). No CONSTANTS.EVENTS entry exists for this yet, so this
     * follows the same `EVENTS.X || 'literal'` fallback already used for
     * UI_BLUEPRINT_CALLOUTS_RENDERED/MODE_CHANGED/OVERLAY_READY above --
     * publish() no-ops safely if CoreEventBus is unavailable.
     */
    if (changed) {
      publish(EVENTS.UI_BLUEPRINT_FRAME_CHANGED || 'ui:blueprint_frame_changed', {
        frame: target,
      });
    }
  }

  /**
   * Front/Back is meaningless in the birdseye and offering both at once reads as
   * two competing view controls, so it is withdrawn while the plan is up.
   *
   * Front/Back rotates the ELEVATION between the door face and its shelves. The
   * birdseye is a top-down plan of the same door: there is no face and no back
   * to swap to, so the control has nothing to act on there. Leaving it on screen
   * invited the customer to operate it and watch nothing meaningful change.
   *
   * 🚨 THE `hidden` ATTRIBUTE ALONE DOES NOT HIDE THIS ELEMENT. `.view-toggle-switch`
   * is an <svg>, and the UA's `[hidden] { display: none }` lives in the HTML
   * user-agent stylesheet, which does not reach the SVG namespace -- the same
   * trap that once rendered the frame switch in photo mode. What actually hides
   * it is `.md-switch[hidden] { display: none }` in section-md-configurator.css,
   * which this element matches because md-toggle-switch.liquid stamps every
   * toggle `class='md-switch {{ classes }}'`. Do NOT remove that rule, and do
   * not assume a bare `hidden` works if this control is ever re-chassised.
   *
   * Layout is safe because all three toggles are absolutely positioned siblings:
   * Front/Back spans the full width at bottom 0, the measure toggle overlays its
   * left at bottom 0, and the frame switch sits at bottom 44. Removing Front/Back
   * from flow moves none of them.
   *
   * State is NOT reset on the way in. A customer who was looking at the shelves
   * returns to the shelves when the elevation comes back; forcing the face would
   * be a second, unasked-for change made behind a hidden control.
   */
  function setFrontBackVisible(on) {
    const fb = document.querySelector('.view-toggle-switch');
    if (!fb) return;
    if (on) fb.removeAttribute('hidden');
    else fb.setAttribute('hidden', '');
  }

  /**
   * The switch is authored in snippets/blueprint-frame-toggle.liquid, not built
   * here -- same division of labour as the photo/measurements toggle, which is
   * also Liquid markup this module merely wires. Building it in JS meant
   * re-deriving the ground circles, the track geometry and the icon placement
   * that the measure toggle already gets right, and the two drifted immediately.
   *
   * This only reveals it and binds the click; it never creates markup.
   */
  function wireFrameSwitch() {
    const btn = document.querySelector('.md-bp-frameswitch');
    if (!btn) return null;
    if (!btn.dataset.mdBpWired) {
      btn.dataset.mdBpWired = '1';
      const flip = () => {
        showFrame(state.frame === 'birdseye' ? 'elevation' : 'birdseye');
      };
      btn.addEventListener('click', flip);
      /* role=switch on an <svg tabindex=0> is focusable but NOT natively
         operable -- unlike the <button> this used to be, it receives no click
         from Enter or Space. Its two siblings handle this the same way; a
         keyboard user would otherwise be able to reach the control and not
         work it, which is a 2.1.1 failure. */
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          flip();
        }
      });
    }
    return btn;
  }

  function setFrameSwitchVisible(on) {
    const btn = document.querySelector('.md-bp-frameswitch');
    if (btn) {
      if (on) btn.removeAttribute('hidden');
      else btn.setAttribute('hidden', '');
    }
    /* Withdrawing the frame switch means there is no birdseye reachable at all
       -- photo mode, a selection with no plan to draw, or teardown -- so
       Front/Back must come back.

       🚨 THIS RUNS OUTSIDE THE `btn` GUARD DELIBERATELY. (emoji-ok: comment, not
       UI.) Front/Back is hidden by
       US, and the frame switch is the only on-screen control that can give it
       back, so gating the restore on that switch still being in the DOM makes
       the recovery depend on the very thing being torn down. No current path is
       known to hit it -- this is an ordering contract, not a fixed bug -- but
       the failure it forecloses is a customer left with no view control at all
       and no way to recover without a reload, which is worth one branch.
       Pinned by "restores Front/Back even if the frame switch is gone from the
       DOM"; moving this inside the guard fails that test. */
    if (!on) setFrontBackVisible(true);
  }

  /**
   * Put the frame the customer's category actually asks about on screen.
   *
   * Door depth, jamb depth and handing only EXIST in plan, so opening one of
   * them is a request to see the plan; width and height belong to the
   * elevation. The map lives in Phase 0 beside CALLOUT_SLOTS because it is the
   * same fact stated once, not a second opinion about it.
   *
   * Categories absent from the map leave the frame alone.
   */
  function selectFrameForCategory(categoryId) {
    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    const map = (geo && geo.MEASUREMENT_CATEGORY_FRAME) || {};
    const want = map[categoryId];
    if (!want || !_framesEl) return;
    // Never flip to a frame that was not built -- the birdseye needs a plan.
    if (!_framesEl.querySelector(`.md-bp-frame[data-frame="${want}"]`)) return;
    if (state.frame === want) return;
    showFrame(want);
  }

  /**
   * Draws the ISOMETRIC birdseye: the same four CAD diagrams the pass-through
   * widget and tooltip use, embedded into the blueprint canvas.
   *
   * 🚨 THE ART DOES NOT SCALE WITH THE SELECTION -- and that is a deliberate,
   * accepted trade. A 5-1/4in door and a 12in door render identically; only the
   * callout numbers change. The drawn plan it replaces WAS to scale, so do not
   * reintroduce a claim that the picture measures anything.
   *
   * Returns anchors in CANVAS space, or null if the diagram module is absent --
   * in which case the caller falls back to the drawn plan rather than showing an
   * empty frame.
   *
   * 🚨 A MISSING HANDING IS NOT A MISSING MODULE, AND THEY MUST NOT SHARE A
   * `return null`. They did, and the second case was never a decision anybody
   * made -- it fell out of one guard doing two jobs. The consequence, reported
   * from production 2026-09-04: a customer who had picked width, depth and jamb
   * but not yet handing got served the OLD drawn plan, silently, exactly as if
   * the CAD library had failed to load. The fallback's own comment says it
   * exists for an absent module; nothing intended it to cover the ordinary
   * mid-configuration state that every customer passes through.
   *
   * There is no neutral isometric to draw -- the CAD ships exactly four
   * variants (lh/rh x in/out) and none of them is a shut door. So an unknown
   * handing renders the lh-in art as an explicit EXAMPLE: dimmed, captioned,
   * and stripped of every handing-dependent number. That is the same convention
   * the pass-through tooltip already uses (`md-pts-diagram--placeholder`), so
   * this is a second instance of an established pattern rather than a new one.
   */
  function drawIsoDetail(svg, plan, geo, containerWidthPx, passThroughText, wallClearanceText, outlineOnly) {
    const API = window.MurphyDoorPassThroughDiagrams;
    // Module or plan genuinely absent -> the drawn plan is the right fallback.
    if (!API || !plan) return null;
    const known = !!(plan.hand && plan.swing);
    // Example art when handing is unknown. lh-in is an arbitrary but FIXED
    // choice: it must not vary, or the drawing would appear to flip for reasons
    // the customer cannot see.
    const h = !known ? 'lh' : (plan.hand === 'Right' ? 'rh' : 'lh');
    const sw = !known ? 'in' : (plan.swing === 'Inswing' ? 'in' : 'out');
    const key = `${h}-${sw}`;
    let iso;
    try { iso = API.createDiagram(h, sw); } catch (e) { iso = null; }
    if (!iso) return null;

    /* BOTH annotations are the diagram's OWN, fed with OUR numbers so the two
       never disagree. Neither is computed here: each arrives as an already
       formatted callout label from md-config-bl-dimension-calculator.js, which
       reads them off the space calculator's payload and validates that payload
       against the current selection. That indirection is the point -- it is why
       adding the second number costs no new maths, and why CLAUDE.md's
       "Pass-Through Math" footgun (the single-door 3/4in reveal re-implemented
       as the French 1/2in one) cannot be reintroduced from this file.

       Wall clearance used to be hardcoded to '' here. It is the head-of-door
       measurement the pass-through widget and tooltip both show on an in-swing
       door, and the birdseye is the surface a customer is most likely to reach,
       so suppressing it withheld the number exactly where it was wanted. An
       out-swing selection still shows nothing, and needs no branch to do so:
       the calculator omits the callout (there is no clearance) and the
       out-swing CAD carries no .ptw-wc-annotation group to write into anyway. */
    /* 🚨 THE EXAMPLE CARRIES NO NUMBERS. Pass-through and wall clearance are
       both functions of the swing, so printing either one onto art the customer
       has not chosen would state a measurement for a door that does not exist
       yet -- the precise failure the placeholder is meant to avoid. Depth and
       jamb still get their callouts: those are handing-INDEPENDENT and already
       chosen, which is why the drawn plan labels them too. */
    /* Traveling Preview chip (2026-09-15): the diagram's own two red
       annotations (.ptw-rug-label, .ptw-wc-annotation) are literally a
       number and a pointer bracket -- exactly what the chip must not show.
       Skip applyMeasurements() entirely rather than pass it blanks: '' would
       still leave the wc-annotation's bracket lines visible (only the TEXT
       is opts-gated there, per pass-through-diagrams.js's applyMeasurements),
       so both are hidden directly instead. The known-handing/full-detail
       path (outlineOnly false, e.g. the open sheet or desktop) is completely
       unchanged. */
    if (outlineOnly) {
      const rugLabel = iso.querySelector('.ptw-rug-label');
      if (rugLabel) rugLabel.textContent = '';
      const wcAnnotation = iso.querySelector('.ptw-wc-annotation');
      if (wcAnnotation) wcAnnotation.style.display = 'none';
    } else {
      try {
        API.applyMeasurements(iso, {
          /* undefined, NOT '' — applyMeasurements writes any string it is given,
             and '' would blank the CAD's own `-` placeholder, leaving an empty
             floor band that reads as a rendering fault rather than as "not
             determined yet". Passing undefined skips the write and leaves the
             dash the artwork ships with, which is the same thing the pass-through
             tooltip shows on its placeholder.

             The known-handing branch keeps its exact previous behaviour. */
          passThroughText: known
            ? (typeof passThroughText === 'string' ? passThroughText : '')
            : undefined,
          wallClearanceText: known
            ? (typeof wallClearanceText === 'string' ? wallClearanceText : '')
            : undefined,
        });
      } catch (e) { /* numbers are a bonus; the drawing still stands without them */ }
    }

    const vb = String(iso.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (vb.length !== 4 || vb.some((n) => !Number.isFinite(n))) return null;
    const [vx, vy, vw, vh] = vb;

    /* Height-constrained: the iso is tall and narrow (215x500), so it is the
       band that binds and the leftover WIDTH becomes the callout gutters. */
    const B = geo.BIRDSEYE || {};
    const top = (B.BAND_TOP || 420) - 120;          // just under the frame rule
    const bottom = B.SAFE_BOTTOM || 1600;           // clear of the slide's controls
    const availH = Math.max(1, bottom - top);
    const availW = geo.CANVAS * 0.62;               // leave the gutters alone
    const scale = Math.min(availW / vw, availH / vh);
    const tx = geo.CANVAS / 2 - (vx + vw / 2) * scale;
    const ty = top - vy * scale;

    // Clip to the diagram's own frame box: its floor bands overflow the viewBox
    // on purpose (they run off-frame in the tooltip too) and would otherwise
    // spill across the blueprint.
    const clipId = `md-bp-iso-clip-${key}`;
    const defs = el('defs');
    const clip = el('clipPath', { id: clipId });
    clip.appendChild(el('rect', { x: vx, y: vy, width: vw, height: vh }));
    defs.appendChild(clip);
    svg.appendChild(defs);

    const g = el('g', {
      class: `md-bp__iso${known ? '' : ' md-bp__iso--placeholder'}`,
      transform: `translate(${tx} ${ty}) scale(${scale})`,
      'clip-path': `url(#${clipId})`,
    });
    // Adopt the diagram's children; the <svg> wrapper itself is not needed and
    // a nested <svg> would re-establish its own viewport and undo the transform.
    while (iso.firstChild) g.appendChild(iso.firstChild);
    svg.appendChild(g);

    /* No on-canvas caption for the placeholder state (removed 2026-09-12) --
       the `md-bp__iso--placeholder` class above still dims the example art,
       and the screen-reader summary below still carries the explanation via
       frameSummary()'s `note` param, so a customer selecting Door Handing is
       the only thing that changes what they see; nothing is said twice. */

    /* Anchors are stored for the LEFT-hand art only. The right-hand diagrams are
       that same art through `scale(-1,1) translate(-C,0)`, so mirror them with C
       read from the transform itself rather than hand-authoring a second pair --
       C differs by swing (248 in, 280 out) and reading it off a screenshot put
       the mirrored anchors out by up to 12 units. */
    let mirrorC = null;
    if (h === 'rh') {
      /* 🚨 QUERY `g`, NOT `iso`. The adoption loop above has already emptied the
         source SVG, so `iso.querySelector` finds nothing and the mirror silently
         never applies -- the dots then land on the LEFT-hand coordinates, which
         is a plausible-looking position on the wrong side of the drawing. */
      const tg = g.querySelector('g[transform]');
      const m = tg && /translate\(\s*(-?[\d.]+)/.exec(tg.getAttribute('transform') || '');
      if (m) mirrorC = -parseFloat(m[1]);
    }
    const table = (geo.ISO_ANCHORS || {})[`lh-${sw}`] || {};
    const out = { __iso: key, __isoPlaceholder: !known };
    Object.keys(table).forEach((id) => {
      const ax = mirrorC === null ? table[id].x : mirrorC - table[id].x;
      out[id] = { x: tx + ax * scale, y: ty + table[id].y * scale };
    });
    return out;
  }

  /* 🚨 GROUP-AWARE STACKING FOR THE ELEVATION'S SHARED RIGHT GUTTER.
     Two production collisions, both from the SAME root weakness: every
     callout used to clamp against `caps.zoomTop` (the zoom-column ceiling)
     in complete ISOLATION from every other callout sharing its gutter --
     each block asked "do I clear the buttons?" and never "do I clear my
     NEIGHBOR?".

     Incident 1 (2026-09-11, fixed via an `ay` retune to 700): the new
     `ro-to-casing-edge-right` (authored ay 1450) and `overall-width`
     (authored ay 1700) both clamped to the same ceiling and landed within a
     couple of canvas units of each other.

     Incident 2 (found via real Safari at a 766x235-class container -- see
     `/deep-uat`): mirroring the elevation for a right-hand door (see
     `mirrorTransform` above) correctly moves `ro-size` and `door-size` into
     the shared right gutter for the FIRST time -- before the mirror fix
     neither ever reached the clamped column on any hand, so this exact
     collision was unreachable. `door-size` (authored ay 860) clamps down to
     land almost on top of `ro-size` (ay 420, never clamped), and both are
     PRIORITY-1 always-shown callouts, so no `ay` retune is safe long-term:
     the NEXT always-shown callout added to this gutter would just recreate
     the same bug. Two blocks need to be aware of EACH OTHER, not just of the
     ceiling.

     This function stacks every gutter's blocks top-down by authored `ay`,
     pushing a block down only as far as its predecessor's `MIN_GAP`-padded
     bottom requires (pass 1 -- a no-op for any gutter that never collides),
     then -- right gutter only -- pulls blocks UP from the bottom so nothing
     crosses the zoom-column ceiling (pass 2). Pass 2 walks back-to-front so
     the pull only cascades onto an earlier block as far as MIN_GAP actually
     forces it to make room for a later block the ceiling already moved --
     never as a blanket shift of blocks that were never at risk. A gutter
     with a single occupant reduces exactly to the old per-block formula:
     pass 1 leaves it at its authored ay (nothing to collide with), and pass
     2's `min(y0, ceiling - belowBaseline)` on that one block is the exact
     clamp the old inline code ran. Zero regression risk for every callout
     that has never shared a gutter.

     Birdseye is deliberately EXCLUDED (see the `if (!bird)` guard at the call
     site). Its per-block positions come from `BIRDSEYE_SLOTS_BY_HANDING`,
     which is already hand-tuned per handing/depth combination specifically
     so its blocks don't collide -- running this stacking pass on top would
     fight that existing tuning rather than complement it. */
  function stackElevationGutters(entries, caps) {
    const MIN_GAP = 30;
    const byGutter = { left: [], right: [] };
    entries.forEach((e) => { byGutter[e.left ? 'left' : 'right'].push(e); });
    /* 🚨 EACH GUTTER HAS ITS OWN CHROME, AND THEY ARE NOT THE SAME HEIGHT.
       The right gutter shares the zoom column (three stacked 44px buttons,
       152px reserve -- caps.zoomTop). The left gutter shares the bottom
       control row (.md-measure-toggle sits at its LEFT edge, one 44px row,
       50px reserve -- caps.chromeTop; its LABEL is taller than the row, so
       TEXT needs the 94px caps.leftTextTop instead). All three exist and are
       already computed
       by deriveChromeCaps -- the bug this fixes is that only zoomTop was ever
       being APPLIED, to the right gutter alone, because before hand-mirroring
       moved high-`ay` callouts (overall-width, ay 1700) into the left gutter
       for a Right-Hand door, nothing authored ever landed at the left gutter's
       floor. `floorBandRuleY` already clamps the DRAWN overall-width RULE
       against chromeTop -- this is the matching clamp for the TEXT BLOCK that
       names it, which had no clamp at all and rendered on top of the
       measure-toggle icons. It uses leftTextTop, NOT chromeTop: the rule is a
       hairline that clears the 44px row, but the text is a stacked block that
       must clear the toggle's label too. See deriveChromeCaps' JSDoc. Reported live: "Overall width" text sitting on
       the toggle row on a Right-Hand door. */
    const ceilingFor = { left: caps && caps.leftTextTop, right: caps && caps.zoomTop };
    Object.keys(byGutter).forEach((gutter) => {
      const list = byGutter[gutter].slice().sort((a, b) => a.authoredAy - b.authoredAy);
      if (!list.length) return;
      let bottom = -Infinity;
      list.forEach((e) => {
        e.stackedY0 = Math.max(e.authoredAy, bottom + MIN_GAP);
        bottom = e.stackedY0 + e.belowBaseline;
      });
      const ceilingTop = ceilingFor[gutter];
      if (Number.isFinite(ceilingTop)) {
        let ceiling = ceilingTop;
        for (let i = list.length - 1; i >= 0; i -= 1) {
          const e = list[i];
          e.stackedY0 = Math.min(e.stackedY0, ceiling - e.belowBaseline);
          ceiling = e.stackedY0 - MIN_GAP;
        }
      }
    });
  }

  function drawLabels(svg, callouts, planAnchors, containerWidthPx, frame, mirrorSides, handingKey, caps, changedIds, entKey, casKey) {
    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    if (!geo) return [];
    const slots = geo.CALLOUT_SLOTS || {};
    // Same trace-name contract as drawBraces' casKey -- see elevationGutters'
    // doc comment for why "always front-casing-standard" was the root cause
    // of the back-view collisions. null (no selection yet) is a real state,
    // not a fallback here: elevationGutters degrades to the tuned defaults.
    const casTrace = casKey ? geo.traces[casKey] : null;
    const entTraceForGutters = entKey ? geo.traces[entKey] : null;
    const U = scaleText(containerWidthPx, 1);
    const TITLE = 10 * U;
    const SUB = 9 * U;
    const VALUE = 15.5 * U;
    /* Leading for a WRAPPED title. Tighter than the title-to-sub step (1.3),
       because those are two different things being separated while these are
       one phrase being folded -- too much air and it stops reading as one
       label. */
    const TITLE_LEADING = 1.2;
    /* Step from a VALUE baseline down to the title of a second measurement
       sharing the block (`extra`). Wider than any within-pair step, because it
       has to read as "a new thing starts here" rather than as more of the
       previous number -- and it has to clear the value's descender, which the
       within-pair steps never have to do. */
    const EXTRA_LEADING = 1.9;

    /* Which gutter columns this frame's text sits in. The elevation's are tuned
       flush to the door art; the birdseye has no art to sit against, so its
       columns are wider -- the value type is the same fixed screen size either
       way, and cramming it into the narrower pair is what would clip it. */
    const B = geo.BIRDSEYE || {};
    const bird = frame === 'birdseye';
    const elevGutters = bird ? null : elevationGutters(geo, casTrace, entTraceForGutters);
    /* elevationGutters widens outward to clear the casing/entablature art,
       with nothing stopping it from narrowing the gutter past what the
       ACTUAL rendering title/value text needs -- see minGutterUnitsForCallouts'
       doc comment. Clamped back OUTWARD whenever that would happen, so
       legible text wins over a perfect art-clearance margin in the rare case
       both can't be had at once.
       🚨 THE FLOOR ITSELF IS CAPPED AT geo.GUTTER_L/R, NOT THE CANVAS EDGE.
       At a very small fitted side (the 766x235 "clears the gallery chrome"
       shape, fittedPx 235) the canvas-unit width even a short title needs
       balloons past what ANY gutter here has ever offered -- "Rough opening"
       alone would demand ~680 units against a 558-unit DEFAULT gutter, with
       or without this PR's widening. Requiring the full uncapped minimum at
       that shape doesn't fix a real regression (it was never legible at that
       extreme before this PR either); it just makes an unrelated pre-existing
       tightness THIS fix's problem, and pushing colL past colR entirely
       dropped every callout in that gutter (caught in exactly this way:
       flipping this cap off failed both `766x235` tests down to 0 lines).
       Capping the floor at the tuned default keeps the new protection scoped
       to what it actually fixes -- elevationGutters' own widening -- and
       never regresses past the SAME baseline tightness that already existed
       at extreme shapes before any of this PR's changes. */
    const CANVAS = geo.CANVAS || 2048;
    const minGutters = bird ? null : minGutterUnitsForCallouts(callouts, slots, mirrorSides, TITLE, VALUE);
    const colL = bird ? B.LABEL_L
      : Math.max(elevGutters.L, Math.min(minGutters.minLeft, geo.GUTTER_L));
    const colR = bird ? B.LABEL_R
      : Math.min(elevGutters.R, Math.max(CANVAS - minGutters.minRight, geo.GUTTER_R));

    const g = el('g', { class: 'md-bp__labels' });
    const drawn = [];

    /* Two passes, not one. RESOLVE first (every callout's gutter, dot and
       block height, with no drawing yet), then -- elevation only -- run
       stackElevationGutters() so a block's final y0 can account for its
       NEIGHBORS in the same gutter, not just its own authored position.
       Birdseye keeps rendering with the exact same per-block clamp it always
       had: its per-block y0 is computed the exact same way it always was,
       just read out of the resolved entry instead of a loop-local variable. */
    const resolved = [];
    callouts.forEach((c) => {
      const slot = slots[c.id];
      if (!slot) return;
      /* A slot belongs to exactly one frame. Without this the birdseye's three
         would also draw onto the elevation, which is the whole thing we are
         undoing. Untagged slots fall back to the elevation so a slot added
         without a frame degrades to today's behaviour rather than vanishing. */
      if ((slot.frame || 'elevation') !== frame) return;

      /* A dot that sits ON a movable rule has to move WITH it -- see
         floorBandRuleY. The slot declares that relationship rather than the
         renderer guessing from the coordinate, the same way `dotFrom` names a
         plan anchor instead of hardcoding one. */
      let rawDot = slot.dot || (planAnchors ? planAnchors[slot.dotFrom] : null);
      /* dotRidesCasingEdge -- CORRECTED 2026-09-12, same root cause as
         elevationGutters above. 'ro-to-casing-edge-left/right' used to carry
         a fixed dot.x (584 / 1440) matching only the FRONT casing's edges;
         on the back view the actual casing legs sit at 552/1496, so the
         leader dot pointed at a spot on the sheet the casing had already
         moved away from. Substituted BEFORE mirroring (below) so the mirror
         reflects the true edge, not the stale one. Falls through to the
         authored fixed x when no casing trace is resolved yet. */
      if (rawDot && slot.dotRidesCasingEdge && casTrace) {
        const edgeX = slot.dotRidesCasingEdge === 'L' ? casTrace.bbox.L : casTrace.bbox.R;
        rawDot = { x: edgeX, y: rawDot.y };
      }
      /* 🚨 THE ELEVATION'S DOTS MIRROR TOO, NOT JUST THE TEXT COLUMN. Every
         elevation dot is a fixed CANVAS point (slot.dot), and drawSilhouettes/
         drawBraces now mirror the ART itself for a right-hand door (see
         mirrorTransform). Mirroring only the gutter column below and leaving
         the dot where it was authored -- what this did before -- sent the
         leader all the way across the sheet to the OTHER side's art on every
         right-hand elevation callout, not only the casing-dependent ones:
         'ro-size' alone used to run from the right gutter to x675, an
         800+-unit diagonal clean across the drawing. Reflecting the dot
         through the same axis the art was mirrored through keeps the leader
         landing on the art it actually points at. Birdseye is unaffected --
         it mirrors through BIRDSEYE_SLOTS_BY_HANDING's absolute per-hand
         overrides instead, which already name the correct side outright. */
      if (!bird && mirrorSides && rawDot) {
        rawDot = { x: 2 * elevationMirrorAxis(geo) - rawDot.x, y: rawDot.y };
      }
      let dot = rawDot && slot.dotRidesRule
        ? { x: rawDot.x, y: floorBandRuleY(geo, caps, U) }
        : rawDot;
      // Same idea, the entablature-width rule instead of the floor band --
      // see entablatureWidthRuleY's doc comment. A ridden rule with no
      // entablature trace (entKey null) yields null here, which the
      // "no dot, no callout" check right below already handles correctly.
      if (dot && slot.dotRidesEntablatureRule) {
        const ridesY = entablatureWidthRuleY(geo, entKey);
        dot = Number.isFinite(ridesY) ? { x: dot.x, y: ridesY } : null;
      }
      // A plan-derived anchor is absent when the plan itself could not be drawn
      // (no handing, no depth) or the span does not exist (no pass-through).
      // Drawing the text with no leader would leave a number pointing at
      // nothing, so the whole block is skipped.
      if (!dot) return;

      /* 🚨 The birdseye MIRRORS with the handing, so a fixed side sends every
         leader across the figure on a right-hand door. Flipping the columns with
         the drawing keeps each leader on its own side of the plan.

         A per-handing override wins outright and is NOT mirrored on top -- it
         already names the absolute side. Out-swing mirrors the figure
         vertically, which moves the anchors but not the slots, so the four
         handings genuinely need four arrangements rather than one mirrored pair.
         See BIRDSEYE_SLOTS_BY_HANDING. */
      const byHand = bird && handingKey && geo.BIRDSEYE_SLOTS_BY_HANDING
        ? (geo.BIRDSEYE_SLOTS_BY_HANDING[handingKey] || {})[c.id]
        : null;
      const centred = byHand && byHand.side === 'center';
      const left = byHand && !centred
        ? byHand.side === 'left'
        : (mirrorSides ? slot.side !== 'left' : slot.side === 'left');
      // `dx` nudges a centred block off the centreline -- the only knob a
      // centred slot has, since it owns no gutter to slide along.
      const x = centred
        ? (B.CX || 1024) + (Number.isFinite(byHand && byHand.dx) ? byHand.dx : 0)
        : (left ? colL : colR);
      // Elevation text runs OUTWARD into the margin; birdseye text runs INWARD
      // from the frame edge, because it has no margin to run into.
      const anchor = centred
        ? 'middle'
        : (bird ? (left ? 'start' : 'end') : (left ? 'end' : 'start'));
      /* A title may be pre-broken into lines by the module that authored the
         copy -- `titleLines` -- because where a phrase breaks is a COPY
         decision, not a layout one ("from jamb" belongs on one line). It is
         never derived here: this renderer has no idea which words go together,
         and a width-based auto-wrap would split them differently at every
         container size.

         🚨 DECLARED BEFORE THE CAP, NOT BESIDE THE <text> IT DRAWS. The
         right-gutter cap below measures how far the block reaches BELOW its
         first baseline, and a wrapped title is most of that reach. Declaring it
         at the render site left the cap reading it from the temporal dead zone
         -- a hard ReferenceError that took every callout on the sheet down, not
         just this one. */
      const titleLines = Array.isArray(c.titleLines) && c.titleLines.length
        ? c.titleLines
        : [c.title];
      const authoredAy = byHand && Number.isFinite(byHand.ay) ? byHand.ay : slot.ay;
      /* Every line BELOW the title's first baseline -- the part of the block
         that can reach the zoom-column buttons on the right, OR a neighboring
         block stacked below this one in the same gutter (see
         stackElevationGutters above). Wrapped titles, suppressed values and a
         second measurement sharing the block all change this: a four-line
         title reaches 177 units lower than a one-line one, a `valueInArt`
         block has no value line to clear, and an `extra` adds a whole
         title+value pair BELOW the value. Miss the extra here and the block
         clears the buttons (or the next block down) by its old height while
         its shelf-depth pair sits on top of them. */
      const wrappedTitle = (titleLines.length - 1) * TITLE * TITLE_LEADING;
      const valueLine = slot.valueInArt ? 0 : VALUE * 1.12;
      const extraLines = c.extra ? TITLE * EXTRA_LEADING + VALUE * 1.12 : 0;
      // The descender allowance belongs to whichever line ends up LAST. Only
      // a block that ends on a title (valueInArt with no extra) needs none.
      const descender = (valueLine || extraLines) ? VALUE * 0.18 : 0;
      const belowBaseline = wrappedTitle
        + (c.sub ? SUB * 1.3 : 0)
        + valueLine + extraLines + descender;
      resolved.push({
        c, slot, x, anchor, dot, titleLines, left, centred, authoredAy, belowBaseline,
      });
    });

    /* 🚨 THE RIGHT GUTTER IS SHARED WITH THE GALLERY'S ZOOM COLUMN, AND CAN BE
       SHARED WITH ANOTHER CALLOUT TOO. Overall width's authored ay parked its
       three lines between the Zoom-in and Reset buttons -- on top of them.
       A per-block clamp alone (what used to run here) is what let a NEW
       callout collide with an existing one instead of the buttons -- see
       stackElevationGutters' own doc comment for both production incidents.
       min() only ever lifts, so a block already clear (overall height, at ay
       420) is untouched, exactly as before. Birdseye is excluded -- its
       positions come from BIRDSEYE_SLOTS_BY_HANDING, already hand-tuned per
       handing so its blocks don't collide. */
    if (!bird) stackElevationGutters(resolved, caps);

    resolved.forEach((entry) => {
      const {
        c, slot, x, anchor, dot, titleLines, left, centred, authoredAy, belowBaseline,
      } = entry;
      let y0 = authoredAy;
      if (!bird) {
        y0 = entry.stackedY0;
      } else if (!left && !centred && caps && Number.isFinite(caps.zoomTop)) {
        y0 = Math.min(y0, caps.zoomTop - belowBaseline);
      }
      let y = y0;

      /* Rendered as tspans inside ONE <text> rather than as sibling <text>
         nodes, so the block stays a single element: `textContent` still returns
         the whole title, CSS still applies once, and anything walking
         g.md-bp__labels still sees one node per callout. Absolute x/y on each
         tspan rather than dy -- dy accumulates differently across renderers
         once text-anchor is not `start`. */
      const titleNode = el('text', {
        x, y, class: 'md-bp__label', 'text-anchor': anchor, 'font-size': TITLE,
      }, titleLines.length > 1 ? null : c.title);
      if (titleLines.length > 1) {
        titleLines.forEach((line, i) => {
          titleNode.appendChild(el('tspan', { x, y: y + i * TITLE * TITLE_LEADING }, line));
        });
        // Later blocks measure from the LAST baseline, same as the sub does.
        y += (titleLines.length - 1) * TITLE * TITLE_LEADING;
      }
      g.appendChild(titleNode);
      if (c.sub) {
        y += SUB * 1.3;
        g.appendChild(el('text', {
          x, y, class: 'md-bp__sub', 'text-anchor': anchor, 'font-size': SUB,
        }, c.sub));
      }
      /* 🚨 `valueInArt` BLOCKS SUPPRESS THE VALUE LINE, NOT THE VALUE. The
         drawing already states the number at the point of measurement, so
         printing it again in the margin says it twice within one glance --
         wall clearance's own annotation sits ~400 units from its block. The
         block keeps its title and its leader, which is the part the art cannot
         supply: a bare "1 3/4″" over a dimension arrow names nothing.

         The callout still CARRIES its label, and two consumers still read it:
         frameSummary() -- the only path a screen reader has, because the
         drawing is aria-hidden -- and drawIsoDetail(), which is what writes the
         number into the art in the first place. Dropping the label upstream
         instead of hiding this one <text> would take both of those with it. */
      if (!slot.valueInArt) {
        y += VALUE * 1.12;
        g.appendChild(el('text', {
          x, y, class: 'md-bp__value', 'text-anchor': anchor, 'font-size': VALUE,
        }, c.label));
      }

      /* A SECOND measurement folded into this block -- today only shelf depth
         under door depth. It reuses the title and value classes rather than
         inventing a quieter treatment: it is a real dimension the customer
         shops with, not an annotation on the one above it, and typographic
         demotion would say otherwise.

         It shares the leader deliberately. Shelf depth has no edge of its own
         in the drawing to point at, so giving it a slot would mean a second
         dot landing on the same leaf the door-depth leader already names. */
      /* 🚨 THE MODIFIER CLASSES ARE LOAD-BEARING, NOT COSMETIC. These texts are
         flat siblings in one `g.md-bp__labels` -- nothing ties a polyline to
         the block it belongs to, so every reader of this group recovers the
         blocks by RUN, treating each `md-bp__label` as the start of a new one.
         An unmarked second title splits the door-depth run in two and hands its
         leader and dot to the shelf-depth half, which is a block claiming
         geometry it does not own. The modifier says "this pair continues the
         block above" in the one place a consumer can actually see it.
         Base classes are kept so styling and `.md-bp__value` counts still
         match. */
      if (c.extra && c.extra.label) {
        y += TITLE * EXTRA_LEADING;
        g.appendChild(el('text', {
          x, y, class: 'md-bp__label md-bp__label--extra',
          'text-anchor': anchor, 'font-size': TITLE,
        }, c.extra.title));
        y += VALUE * 1.12;
        g.appendChild(el('text', {
          x, y, class: 'md-bp__value md-bp__value--extra',
          'text-anchor': anchor, 'font-size': VALUE,
        }, c.extra.label));
      }

      /* 🚨 THE LEADER LEAVES FROM THE CORNER NEAREST THE DOT, NOT FROM A FIXED
         ONE. It used to start at the block's OUTER x at the value baseline, so
         on a left-hand block it set off from the far side and ran back
         UNDERNEATH its own three lines before it even reached the drawing -- and
         it always left from the bottom, so a dot ABOVE the block got a leader
         that dropped, doubled back and climbed.

         Both axes are chosen independently:
           x -- the INNER edge (the side facing the figure)
           y -- the block's top if the dot is above it, its baseline if below,
                otherwise the dot's own y, which makes the leader run flat.

         Width is estimated from the text advance rather than getBBox: this runs
         BEFORE the svg is in the document, so getBBox would measure zero here
         (the same reason the WALL gate estimates instead of measuring). */
      const adv = (str, fs, ls) =>
        str ? (String(str).length * 0.62 * fs + (String(str).length - 1) * ls) : 0;
      const blockW = Math.max(
        ...titleLines.map((line) => adv(line, TITLE, 0)),
        adv(c.sub, SUB, 0),
        // A suppressed value occupies no width -- measuring it anyway would
        // stand the leader off from a line that is not there.
        slot.valueInArt ? 0 : adv(c.label, VALUE, 0),
        // The extra pair is often the WIDEST part of the block ("Shelf depth"
        // outruns "Door depth"), and the leader exits from the block's edge --
        // so leaving it out of the measurement routes the leader back through
        // its own text.
        c.extra ? adv(c.extra.title, TITLE, 0) : 0,
        c.extra ? adv(c.extra.label, VALUE, 0) : 0
      );
      /* Clear of the glyph box, not ON its edge. getBBox includes the
         descender, so exiting exactly at the value baseline still clipped the
         text -- measured on the preview, three of the twelve leaders ran back
         through their own number. */
      const PAD_X = 4 * U;
      const PAD_Y = 0.34 * VALUE;          // descender + a hair
      /* 🚨 DERIVE THE TEXT'S SPAN FROM ITS ANCHOR -- the two frames anchor in
         OPPOSITE directions. The birdseye runs text INWARD from the frame edge
         ('start' on the left), the elevation runs it OUTWARD into its margin
         ('end' on the left). Assuming the birdseye's direction for both put the
         elevation's exit a full block-width INSIDE the drawing, so its leaders
         set off from mid-artwork and read as detached from their labels.
         Computed from the anchor, this is correct for either frame and for a
         centred block, with no special case. */
      let spanL;
      let spanR;
      if (anchor === 'start') { spanL = x; spanR = x + blockW; }
      else if (anchor === 'end') { spanL = x - blockW; spanR = x; }
      else { spanL = x - blockW / 2; spanR = x + blockW / 2; }
      // Leave from whichever edge faces the dot; if the dot is directly above or
      // below the block, drop straight out of it rather than sidestepping first.
      const innerX = dot.x > spanR ? spanR + PAD_X
        : (dot.x < spanL ? spanL - PAD_X : dot.x);
      const blockTop = y0 - TITLE * 0.8;
      const exitY = dot.y < blockTop ? blockTop - PAD_Y
        : (dot.y > y ? y + PAD_Y : dot.y);
      const toDot = dot.x >= innerX ? 1 : -1;
      const run = Math.abs(dot.x - innerX);
      const sx = innerX + Math.min(7 * U, run * 0.18) * toDot;
      g.appendChild(el('polyline', {
        points: `${innerX},${exitY} ${sx},${exitY} ${dot.x},${dot.y}`,
        class: 'md-bp__leader',
      }));
      g.appendChild(el('circle', { cx: dot.x, cy: dot.y, r: 4.2 * U, class: 'md-bp__leader-dot' }));

      /* The change-marker: a one-shot ring that appears, holds, then fades
         (see .md-bp__change-marker's keyframes in section-md-configurator.css)
         around the exact point on the drawing this measurement is taken
         from. Centred on the SAME dot the leader points at rather than the
         text block, so it works identically for a normal callout (value
         drawn in the gutter) and a `valueInArt` one like pass-through/
         wall-clearance (value baked straight into the isometric art by
         drawIsoDetail) -- both still get a dot here, so both get a marker
         with no frame- or slot-specific branching needed.

         🚨 NEGATIVE `animation-delay` IS WHAT KEEPS A REDRAWN MARKER FROM
         RESTARTING. `changedIds` (from computeChangedCalloutIds) is keyed by
         id -> ms already elapsed since the real change, not a plain
         changed/unchanged flag -- every render() rebuilds this circle as a
         BRAND NEW element (render() always tears the whole SVG down), so
         without this the animation would visibly restart from 0% on every
         one of the several renders one customer action can trigger. A
         negative delay tells the animation to begin as if it had already
         been running for that long, so a marker that is, say, 900ms into
         its 6s lifecycle keeps reading as 900ms in across a fresh element,
         not as a brand-new appearance. Set as a separate inline
         `animation-delay` rather than overriding the whole `animation`
         shorthand, so it composes with (rather than replaces) the
         duration/timing-function/fill-mode the stylesheet rule already
         declares -- the cascade lets a later, more specific longhand win
         over an earlier shorthand for just that one sub-property. */
      if (changedIds && Object.prototype.hasOwnProperty.call(changedIds, c.id)) {
        g.appendChild(el('circle', {
          cx: dot.x, cy: dot.y, r: 15 * U, class: 'md-bp__change-marker',
          'data-callout-id': c.id,
          style: `animation-delay: -${changedIds[c.id]}ms`,
        }));
      }

      drawn.push(c.id);
    });

    svg.appendChild(g);
    return drawn;
  }
  /** The text that actually reaches the customer's eye for one callout --
   * title is fixed per id, so it is deliberately left out: a signature that
   * included it would never change and would defeat the whole comparison. */
  function calloutSignature(c) {
    return `${c.label || ''}|${c.sub || ''}|${(c.extra && c.extra.label) || ''}`;
  }

  /**
   * How long a change-marker stays on screen (fade-in + hold + fade-out),
   * in ms. 🚨 MUST MATCH `.md-bp__change-marker`'s `animation: mdBpChangeMarker
   * Ns ...` duration in section-md-configurator.css -- this is the single
   * source of truth for "how far into its animation is a redrawn marker",
   * and the two are unrelated files a CSS-only edit would not think to
   * check. If the CSS duration ever changes, change this to match.
   */
  const CHANGE_MARKER_DURATION_MS = 6000;

  /**
   * Which callouts should show a change-marker RIGHT NOW, and how far into
   * that marker's lifetime this render falls -- keyed by callout id, valued
   * by elapsed ms since the underlying value actually changed. Always
   * primes state's baseline for next time as a side effect, even when it
   * reports nothing active.
   *
   * 🚨 THIS IS NOT "WHAT CHANGED SINCE THE LAST RENDER" -- see
   * state.calloutChangedAt's doc comment for why that question is the
   * wrong one to ask. A render() can legitimately happen 400ms+ after the
   * customer's actual click (space-recalculation follow-ups, etc.), well
   * outside any single microtask burst, so "changed since the immediately
   * prior render" misses real changes that already got consumed by an
   * intermediate render's own commit. Tracking wall-clock recency instead
   * means every render within CHANGE_MARKER_DURATION_MS of a real change
   * redraws that id's marker, independent of how many renders happened in
   * between or how far apart in real time they landed.
   *
   * A `null` baseline (fresh "mode just turned on" render, or the very
   * first one ever) and an in-progress bulk restore (`?config=` link, Will
   * It Fit prefill, simplified-mode defaults -- see state.bulkRestoreActive)
   * both mean "not a customer click", so both report zero active markers
   * even though real text differs underneath, or a prior real change is
   * still technically inside its window -- a customer did not click
   * anything in either case, and re-showing a saved link is not the moment
   * to flash every measurement on the sheet as if it just changed.
   */
  function computeChangedCalloutIds(callouts) {
    const next = {};
    (callouts || []).forEach((c) => {
      next[c.id] = calloutSignature(c);
    });
    const prev = state.calloutSignatures;
    const now = Date.now();
    if (prev && !state.bulkRestoreActive) {
      Object.keys(next).forEach((id) => {
        if (Object.prototype.hasOwnProperty.call(prev, id) && prev[id] !== next[id]) {
          state.calloutChangedAt[id] = now;
        }
      });
    }
    // Prune expired entries every call so this never grows unbounded across
    // a long shopping session.
    Object.keys(state.calloutChangedAt).forEach((id) => {
      if (now - state.calloutChangedAt[id] >= CHANGE_MARKER_DURATION_MS) {
        delete state.calloutChangedAt[id];
      }
    });
    /* 🚨 COMMITTING `next` SYNCHRONOUSLY HERE WOULD ERASE THE MARKER THE INSTANT
       A SECOND render() FIRES FOR THE SAME CUSTOMER ACTION. Multiple event
       handlers routinely react to one selection within the same synchronous
       stack -- e.g. the category's own SELECTION_APPLIED handler and the
       BUSINESS_SPACE_CALCULATION_COMPLETED follow-up (see the subscriber
       below) both call render(), and render() tears the frames down and
       rebuilds every single time, so only the LAST of those renders' output
       ever reaches the DOM. A synchronous commit makes the FIRST render
       correctly detect the change while the SECOND (same data, but now
       diffing against the baseline the first just wrote) sees nothing
       changed -- and the second is the one the customer actually sees.

       Deferring the commit to a microtask means every render() in that same
       synchronous burst diffs against the SAME pre-burst baseline, so
       whichever render turns out to be the last one drawn still carries the
       marker. Scheduled independently on every call rather than cancelled/
       replaced: microtasks resolve FIFO, so no matter how many renders fire
       in a row, the LAST one's commit is always the LAST to land.

       🚨 MERGED INTO THE BASELINE, NEVER USED TO REPLACE IT. Verified live on
       production: a Width/Height stepper press does not settle the applied
       selection in one hop -- the category briefly renders with ZERO usable
       callouts (measured: a real customer-facing click produced a render at
       ~50-150ms where `callouts` was empty before the real values landed at
       ~350ms), and compact mode (COMPACT_MAX_PRIORITY filtering, see render())
       drops the same lower-priority ids whenever the container narrows. Either
       one means a real render's `next` can legitimately be missing an id the
       baseline already knows about. A REPLACING commit (`state.calloutSignatures
       = next`) would erase that id from the baseline entirely, so the next
       time it reappears with a genuinely different value, the diff sees no
       `prev` entry to compare against and treats it as "new" rather than
       "changed" -- the customer's real change goes permanently unmarked, not
       just delayed. Merging (`Object.assign({}, state.calloutSignatures,
       next)`) means an id this particular render didn't touch simply keeps
       its last REAL value in the baseline, ready to diff correctly whenever
       it next appears.

       🚨 THE EPOCH CHECK IS WHAT KEEPS THE MERGE FROM UNDOING setActive()'S
       "FRESH LOOK" RESET. Read fresh (`state.calloutEpoch`) at COMMIT time,
       compared against the value captured here at SCHEDULE time: if
       setActive() bumped the epoch and nulled the baseline in between (a
       customer closing and reopening measurement mode while this commit was
       still in flight), this commit belongs to a look that no longer exists
       and must be discarded outright -- merging it in would leak pre-reset
       history back through the very door the reset was supposed to close.
       Every render triggered by the reset itself (via applyMode() ->
       render(), synchronous, before any other code runs) captures the NEW
       epoch and merges cleanly starting from the null the reset just set. */
    const epoch = state.calloutEpoch;
    Promise.resolve().then(() => {
      if (state.calloutEpoch !== epoch) return;
      state.calloutSignatures = Object.assign({}, state.calloutSignatures, next);
    });
    const active = {};
    if (!state.bulkRestoreActive) {
      Object.keys(state.calloutChangedAt).forEach((id) => {
        active[id] = now - state.calloutChangedAt[id];
      });
    }
    return active;
  }

  /**
   * Traveling Preview compact chip (2026-09-15): apo_style mobile collapses
   * the gallery to a chip as short as ~124px tall (section-md-apo-mode.css's
   * [data-preview-compact] clamp) whenever a measurement category is open —
   * exactly when this module's own dimension callouts want to draw. The
   * leader lines and gutter text were authored against the ~370-435px
   * container this file's COMPACT_BREAKPOINT_PX comment documents, never
   * against a box a third that tall, so they collide there regardless of
   * priority filtering. Reported live: front elevation AND the isometric
   * plan both unreadable in the chip.
   *
   * Scoped to apo_style + <=800px + NOT the open preview sheet — the sheet
   * (insideSheetCanvas() below) reparents the SAME .image-layers to near
   * full screen width specifically so it can carry the full desktop-
   * equivalent drawing; only the collapsed chip is too small for it.
   * Desktop and the classic/dual mobile experience (no chip/sheet system)
   * are both untouched by construction.
   */
  function isTravelingPreviewChip(container) {
    if (!container) return false;
    if (insideSheetCanvas(container)) return false;
    const wrapper = container.closest('.murphy-door-configurator-wrapper');
    if (!wrapper || wrapper.getAttribute('data-configurator-experience') !== 'apo_style') {
      return false;
    }
    return !!(window.matchMedia && window.matchMedia('(max-width: 800px)').matches);
  }

  /**
   * True once md-config-ui-mobile-preview-sheet.js has reparented the real
   * .image-layers <li> into the open Traveling Preview sheet's canvas
   * (snippets/configurator-mobile-preview-sheet.liquid's
   * [data-preview-sheet-canvas]). That box is full-bleed screen width — "big
   * enough to handle it" per Ryan, 2026-09-15 — so the chip's outline-only
   * restriction never applies here, and the width-based COMPACT_BREAKPOINT_PX
   * filter below is also bypassed so the sheet always matches the desktop
   * (full-detail) rendering rather than following the same <400px heuristic
   * that a narrow phone screen would otherwise trigger.
   */
  function insideSheetCanvas(container) {
    return !!(container && container.closest('[data-preview-sheet-canvas]'));
  }

  /**
   * The collapsed chip's "there are measurements in here" affordance.
   *
   * WHY: isTravelingPreviewChip() above deliberately draws NO dimension numbers
   * in the chip -- the brace/leader/label system does not fit a ~124-170px box.
   * That is correct, but it leaves the customer with a silhouette and no cue
   * that measurements exist at all, nor that opening the sheet is what reveals
   * them. Ryan, 2026-09-17: put the measuring-tape icon dead centre on the
   * thumbnail, in that state only. It is a signpost, not data -- the real
   * numbers still appear only in the open sheet.
   *
   * The art is CLONED from an instance the page has already rendered, rather
   * than re-declared here, so `snippets/icon-measuring-tape.liquid` stays the
   * one source of truth and a future edit to it reaches this too.
   *
   * 🚨 DO NOT narrow this to `#icon-measuring-tape-template`. That id exists
   * only where `sections/main-product.liquid` renders it, and it is NOT on the
   * configurator PDP -- measured on the local dev server (theme 188331720749):
   * the template div was absent while the icon itself was on the page several
   * times over. This snippet is rendered at many call sites (the configurator
   * section itself, the measurement-mode toggle, willitfit, install-info), all
   * from the same source, so any instance is the same art. Prefer the dedicated
   * template when it exists, then fall back to any rendered instance.
   *
   * 🚨 No instance anywhere => no icon, never a throw. This is decoration on a
   * drawing that must keep rendering; a missing optional icon must not take the
   * blueprint down with it.
   *
   * 🚨 Sized from fittedPx, NOT from a getBoundingClientRect() on anything
   * inside .murphy-door-configurator__image-layers. That element is CSS
   * `scale()`d by the gallery's zoom, so a rect read there bakes the current
   * zoom level into the icon permanently (the documented zoom-corruption trap
   * that already bit scaleText()). fittedPx comes from naturalContainerRect(),
   * which reads offsetWidth/offsetHeight off the untransformed ancestor.
   */
  function drawChipTapeIcon(svg, fittedPx) {
    if (!svg) return null;

    let template = null;
    try {
      /* Selector order is the preference order: the dedicated template first
         (pages that have one), then any rendered instance of the same snippet. */
      template = document.querySelector(
        '#icon-measuring-tape-template svg, svg.icon-measuring-tape'
      );
    } catch (err) {
      return null;
    }
    if (!template) {
      isDev() && console.warn(
        '[BlueprintOverlay] no icon-measuring-tape instance on the page; chip icon skipped'
      );
      return null;
    }

    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    const canvas = (geo && geo.CANVAS) || 2048;
    const cx = (geo && geo.CX) || canvas / 2;
    const cy = canvas / 2;

    /* A share of the drawing's fitted side, clamped at both ends: proportional
       alone would vanish on the shortest chip and crowd the tallest. */
    const targetPx = Math.max(
      CHIP_TAPE_MIN_PX,
      Math.min(CHIP_TAPE_MAX_PX, (fittedPx || 0) * CHIP_TAPE_FRACTION)
    );
    const size = scaleText(fittedPx, targetPx);
    if (!(size > 0)) return null;

    const group = el('g', { class: 'md-bp__tape', 'aria-hidden': 'true' });

    /* The chip sits over product photography, so the icon cannot rely on the
       backdrop for contrast -- a disc gives it a known ground in both
       directions (white icon on dark disc, thin light ring against a dark
       photo). Radius/stroke are canvas units, so they must scale with the
       drawing; only the colours live in CSS. */
    group.appendChild(el('circle', {
      class: 'md-bp__tape-disc',
      cx: cx,
      cy: cy,
      r: size * 0.62,
      'stroke-width': scaleText(fittedPx, 1.5),
    }));

    /* cloneNode, not innerHTML: it preserves the SVG namespace on every child
       without a parse round-trip, and this is our own DOM, not text. */
    const icon = template.cloneNode(true);
    /* Drop the shared `icon` class deliberately. Nothing in the blueprint
       should inherit the theme-wide icon sizing rules (several set an explicit
       width/height, and a CSS length BEATS the width/height attributes set
       below, which would pin this to 20px). The art is unaffected -- the paths
       carry fill="currentColor" and take their colour from .md-bp__tape-icon. */
    icon.setAttribute('class', 'md-bp__tape-icon');
    icon.setAttribute('x', cx - size / 2);
    icon.setAttribute('y', cy - size / 2);
    icon.setAttribute('width', size);
    icon.setAttribute('height', size);
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    if (!icon.getAttribute('viewBox')) icon.setAttribute('viewBox', '0 0 512 512');
    group.appendChild(icon);

    svg.appendChild(group);
    return group;
  }

  function render() {
    // Derived, not remembered — see currentView().
    state.view = currentView();
    const container = viewContainer(state.view);
    if (!container) return;

    // Belt and braces: clear any stale drawing left in the OTHER container, so a
    // flip can never leave two blueprints alive in the DOM at once.
    const other = viewContainer(state.view === 'back' ? 'front' : 'back');
    if (other) {
      const stale = other.querySelector('.md-bp-frames');
      if (stale) stale.remove();
    }

    let frames = container.querySelector('.md-bp-frames');
    if (!state.active) {
      if (frames) frames.remove();
      setFrameSwitchVisible(false);
      _framesEl = null;
      _frameSwitch = null;
      return;
    }

    const geo = window.MurphyDoorConfigurator.blueprintGeometry;
    const calc = window.MurphyDoorConfigurator.dimensionCalculator;
    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!geo || !calc || !conductor) {
      isDev() && console.warn('[BlueprintOverlay] dependencies missing; not rendering');
      return;
    }

    let applied;
    try {
      applied = conductor.getAppliedSelectionsMap();
    } catch (err) {
      isDev() && console.error('[BlueprintOverlay] could not read selections', err);
      return;
    }

    const result = calc.computeCallouts(applied, {
      view: state.view,
      includeProvisional: includeProvisional(),
      // Pass-through is READ from md-config-bl-space-calculator, never
      // re-derived. Null until that module has published once, at which point
      // the callout simply appears on the next render.
      spaceData: state.spaceData,
    });

    const containerRect = naturalContainerRect(container);
    // widthPx drives the COMPACT layout breakpoint -- that is genuinely about
    // horizontal room, so it stays the width. fittedPx drives every px<->canvas
    // conversion, and must be the side the SVG actually scales to.
    const widthPx = containerRect.width || 0;
    const fittedPx = Math.min(containerRect.width, containerRect.height) || widthPx;
    const safeBottomPx = deriveSafeBottomCanvas(containerRect, fittedPx, geo);
    const caps = deriveChromeCaps(containerRect, fittedPx, geo);
    // Traveling Preview: the open sheet is full screen width specifically so
    // it can carry the same detail desktop gets -- bypass the width-based
    // filter there rather than let a phone narrower than 400px quietly drop
    // to the same priority<=2 set the tiny chip would. See insideSheetCanvas().
    const insideSheet = insideSheetCanvas(container);
    const compact = !insideSheet && widthPx > 0 && widthPx < COMPACT_BREAKPOINT_PX;
    // The collapsed chip itself is smaller still (as low as ~124px tall) --
    // no amount of priority filtering fits a leader+label system into that,
    // so it gets outlines only. See isTravelingPreviewChip()'s doc comment.
    const chipOutlineOnly = isTravelingPreviewChip(container);

    // At the narrowest container the seven blocks stack tighter than they read.
    // Priority is the drop order, and the authoritative dimensions (priority 1)
    // are the ones that survive. Screen readers still get this full (unfiltered
    // by chipOutlineOnly) list via frameSummary() below even when nothing is
    // drawn on screen -- the chip's SVG is aria-hidden either way, so the only
    // path to these numbers for an AT user was already the sr-only summary.
    const callouts = compact
      ? result.callouts.filter((c) => c.priority <= COMPACT_MAX_PRIORITY)
      : result.callouts;

    /* Which callouts currently have an active change-marker, and how many ms
       into that marker's lifetime this render falls (id -> elapsed ms) --
       drawLabels() draws a marker on exactly these ids, in EITHER frame one
       appears in. Computed once here (not per-frame) so the elevation and
       birdseye/isometric drawings agree on exactly which measurements are
       currently marked, and at the same elapsed offset. */
    const changedCalloutIds = computeChangedCalloutIds(callouts);

    if (frames) frames.remove();
    frames = document.createElement('div');
    frames.className = 'md-bp-frames';

    const slots = geo.CALLOUT_SLOTS || {};
    // The entablature the customer picked, by trace NAME -- see drawBraces.
    const entKey = state.view === 'back'
      ? result.context.backEntablatureKey
      : result.context.frontEntablatureKey;
    // Same contract, the casing -- see elevationGutters' doc comment for why
    // this can no longer be a single front-view constant.
    const casKey = state.view === 'back'
      ? result.context.backCasingKey
      : result.context.frontCasingKey;

    /* Right-hand doors mirror the elevation, same as the birdseye plan already
       mirrors below (see mirrorSides there). Read off result.context, not
       result.plan: the plan is null until Door Depth is also chosen, but hand
       alone is enough to know which way the elevation's own asymmetric casing
       and entablature art actually sit. */
    const mirrorElevation = result.context.hand === 'Right';

    // ---- FRAME 1: ELEVATION (unchanged drawing, minus the plan) ----
    const elevSvg = makeFrameSvg(geo, 'elevation');
    drawSilhouettes(elevSvg, result.context, state.view, mirrorElevation);
    // Traveling Preview chip: outline only -- the brace/leader dimension
    // system below is what collides in a ~124-170px box. drawSilhouettes()
    // above already drew the door/opening outline; frameSummary() still
    // gets the real (unfiltered-by-this) callouts for screen readers.
    let elevIds = [];
    if (!chipOutlineOnly) {
      drawBraces(elevSvg, fittedPx, entKey, caps, mirrorElevation, casKey);
      elevIds = drawLabels(elevSvg, callouts, null, fittedPx, 'elevation', mirrorElevation, null, caps, changedCalloutIds, entKey, casKey);
    } else {
      // Appended LAST so it paints above the silhouette -- SVG paint order is
      // document order.
      drawChipTapeIcon(elevSvg, fittedPx);
    }
    frames.appendChild(frameShell(
      'elevation', elevSvg, frameSummary(callouts, slots, 'elevation', 'Elevation')
    ));

    // ---- FRAME 2: BIRDSEYE ----
    // Only exists when there is a plan to draw. Depth and width are both
    // required for that, so an untouched configurator shows one frame and no
    // switch -- the second appears the moment it has something to say.
    let birdIds = [];
    if (result.plan) {
      const birdSvg = makeFrameSvg(geo, 'birdseye');
      drawBirdseyeChrome(birdSvg, geo);
      // The plan must be drawn BEFORE its labels: it returns the anchor points
      // the depth, jamb and pass-through leaders land on, and those three move
      // with the selection rather than sitting at fixed canvas coordinates.
      /* ISO FIRST, drawn plan as the fallback. The iso needs the diagram module;
         if it is absent (or its art ever fails to parse) the frame silently
         reverts to the plan rather than rendering empty. */
      /* Read from the FILTERED list, which is what compact mode already trimmed.
         Both of these are priority 2, so both survive COMPACT_MAX_PRIORITY --
         but taking them from `callouts` rather than `result.callouts` keeps the
         annotations honest if that threshold is ever tightened: the drawing
         would go quiet along with the text instead of contradicting it. */
      const byId = (id) => (callouts || []).filter((c) => c.id === id)[0];
      const ptCallout = byId('pass-through');
      const wcCallout = byId('wall-clearance');
      const planAnchors =
        drawIsoDetail(
          birdSvg, result.plan, geo, fittedPx,
          ptCallout && ptCallout.label,
          wcCallout && wcCallout.label,
          // Traveling Preview chip: same outline-only treatment as the
          // elevation above, applied to the iso CAD's own two baked-in red
          // numbers (pass-through/wall-clearance) rather than to OUR
          // drawLabels() leaders, which is why this travels as a param into
          // drawIsoDetail instead of being handled here.
          chipOutlineOnly
        ) ||
        drawPlanDetail(birdSvg, result.plan, fittedPx, birdseyeLayout(geo, safeBottomPx), chipOutlineOnly);
      if (planAnchors) {
        // Right-hand doors mirror the plan, so the label columns mirror too.
        const mirrorSides = !!(result.plan && result.plan.hand === 'Right');
        // `${hand}-${swing}`, lowercased -- null until BOTH are chosen, which is
        // also exactly when the plan is drawn shut and the base slots apply.
        /* The iso frame gets its OWN slot table -- the drawn plan could share the
           centre column, the isometric occupies it. planAnchors.__iso is set only
           by drawIsoDetail, so the plan fallback keeps its original slots. */
        const handingKey = planAnchors.__iso
          ? `iso-${planAnchors.__iso}`
          : (result.plan && result.plan.hand && result.plan.swing
              ? `${result.plan.hand}-${result.plan.swing}`.toLowerCase()
              : null);
        if (!chipOutlineOnly) {
          birdIds = drawLabels(birdSvg, callouts, planAnchors, fittedPx, 'birdseye', mirrorSides, handingKey, caps, changedCalloutIds);
        } else {
          // Ryan asked for the affordance on BOTH measurement frames: the chip
          // shows one at a time via the frame switch, so an icon on only the
          // elevation would vanish the moment a customer flipped to the iso.
          drawChipTapeIcon(birdSvg, fittedPx);
        }
        frames.appendChild(frameShell(
          'birdseye', birdSvg,
          frameSummary(
            callouts, slots, 'birdseye', 'Birdseye view',
            // Mirrors the on-canvas caption. Derived from planAnchors, not from
            // result.plan, so it can never disagree with the art that was drawn.
            planAnchors && planAnchors.__isoPlaceholder
              ? 'Example door shown; select Door Handing to see your own.'
              : null
          )
        ));
      }
    }

    container.appendChild(frames);
    _framesEl = frames;

    /* The switch exists only when there are two frames to move between, and it
       mounts in the CHROME beside the Front/Back switch -- NOT over the drawing.
       Overlaying it would force the plan to shrink back down to clear it, which
       is the exact constraint the split was undoing. It is a view control, the
       same kind of thing Front/Back is, so it belongs in the same row. */
    const hasBird = !!frames.querySelector('.md-bp-frame[data-frame="birdseye"]');
    _frameSwitch = hasBird ? wireFrameSwitch() : null;
    setFrameSwitchVisible(hasBird);
    // Re-assert the frame across re-renders; showFrame falls back to the
    // elevation on its own if the birdseye went away with the selection.
    showFrame(state.frame);

    const drawnIds = elevIds.concat(birdIds);

    // The drawing is decorative to assistive tech (aria-hidden). The numbers are
    // published so a text surface can consume them -- a screen reader gets
    // nothing from an SVG dimension line, and "show more measurements" that only
    // works for sighted users is half a feature.
    publish(EVENTS.UI_BLUEPRINT_CALLOUTS_RENDERED || 'ui:blueprint_callouts_rendered', {
      view: state.view,
      // `extra` travels with the callout it shares a block with, so a consumer
      // that renders these to text gets the shelf depth too rather than
      // silently dropping half of what the sheet shows.
      callouts: callouts.map((c) => ({
        id: c.id, title: c.title, sub: c.sub, label: c.label, extra: c.extra || null,
      })),
      drawn: drawnIds,
      frame: state.frame,
      hasBirdseye: !!birdIds.length || !!(result.plan),
      plan: result.plan,
      suppressed: result.suppressed,
      compact,
      chipOutlineOnly,
      module: 'md-config-ui-blueprint-overlay',
      timestamp: Date.now(),
    });

    isDev() &&
      console.log(
        `[BlueprintOverlay] drew ${drawnIds.length}/${callouts.length} callouts ` +
        `(${compact ? 'compact' : 'full'}${chipOutlineOnly ? ', chip outline-only' : ''}), ` +
        `plan ${result.plan ? 'yes' : 'no'}, ` +
        `${result.suppressed.length} suppressed`,
        result.suppressed
      );
  }

  // ==========================================================================
  // Mode
  // ==========================================================================

  function applyMode() {
    ['front', 'back'].forEach((view) => {
      const container = viewContainer(view);
      if (!container) return;
      container.classList.toggle('md-blueprint-active', state.active);
    });

    // The Front/Back switch and this toggle are SIBLINGS of the view containers,
    // not descendants, so .md-blueprint-active cannot reach them. Blueprint mode
    // lightens the ground beneath the whole slide, so that chrome needs its
    // contrast restored -- hence a second class on the shared parent.
    const chrome = _button && _button.parentElement;
    if (chrome) chrome.classList.toggle('md-blueprint-mode', state.active);

    if (_button) {
      // aria-checked carries the state; the label stays STABLE. Rewriting the
      // label on a role=switch double-announces ("Hide measurements, on") and
      // fights the control's own semantics -- the same reason the Front/Back
      // switch keeps one label and flips aria-checked.
      _button.setAttribute('aria-checked', state.active ? 'true' : 'false');
      _button.classList.toggle('is-active', state.active);
      // Drive the SHARED slider class the Front/Back switch uses, so the thumb
      // animation is single-sourced rather than reimplemented.
      const slider = _button.querySelector('.toggle-slider');
      if (slider) slider.classList.toggle('active', state.active);
    }
    render();
  }

  function setActive(next, source) {
    const target = !!next;
    if (target === state.active) return;
    state.active = target;
    /* Entering measurement mode is a fresh look, so it starts on the elevation.
       The caller then puts up whichever frame the customer's category asks for.
       Leaving does NOT preserve the frame -- the next entry should behave the
       same way every time. */
    if (target) {
      state.frame = 'elevation';
      // Re-entering measurement mode is a fresh look too (see the comment
      // just above) -- there is no meaningful "before" to diff against for
      // the change-marker highlight yet, so clear the baseline rather than
      // let it compare against whatever was on screen before the customer
      // last closed measurement mode. The epoch bump is what makes this
      // reset stick even against a same-tick-stale commit already in
      // flight from before it -- see computeChangedCalloutIds' epoch check.
      // calloutChangedAt clears alongside it so a marker from the PREVIOUS
      // look never carries over and redraws on this fresh one's first paint.
      state.calloutSignatures = null;
      state.calloutEpoch += 1;
      state.calloutChangedAt = {};
    }
    applyMode();
    publish(EVENTS.UI_BLUEPRINT_MODE_CHANGED || 'ui:blueprint_mode_changed', {
      active: state.active,
      source: source || 'unknown',
      module: 'md-config-ui-blueprint-overlay',
      timestamp: Date.now(),
    });
    isDev() && console.log(`[BlueprintOverlay] mode -> ${state.active ? 'ON' : 'OFF'} (${source})`);
  }

  /**
   * Bulk restoration (?config= URL, Will It Fit prefill, simplified-mode /
   * gate-card defaults) applies many categories back-to-back through the
   * exact same SELECTION_APPLIED pipeline a live click uses. Scoped to events
   * carrying a `context` field: only the bulk-apply publish sites in
   * md-config-bl-cascade-auto-selection-coordinator.js set one
   * (`url_restoration`, `willitfit_prefill`, `simplified_mode`) — an ordinary
   * single-click cascade (e.g. matching a wood to a composite category)
   * publishes BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED via
   * scheduleTermination() with no `context` at all, so it is untouched here.
   * Same convention md-config-bl-gate-card-coordinator.js already uses to
   * tell a restoration completion apart from a generic one.
   */
  function isBulkRestoreEvent(data) {
    return Boolean(data && data.context);
  }

  function handleBulkApplyStarted(data) {
    if (!isBulkRestoreEvent(data)) return;
    state.bulkRestoreActive = true;
  }

  /**
   * Loading a restored configuration is a fresh look too, so — like entering
   * measurement mode by hand always starts on the elevation — it always
   * starts on the picture, regardless of which categories the link happened
   * to include or what order they were applied in. Without this, the final
   * on-screen state during the bulk apply was whichever category got applied
   * last (JSON key order for a ?config= link), so a shared link "sometimes"
   * landed on the measurements view instead of the door.
   */
  function handleBulkApplyCompleted(data) {
    if (!isBulkRestoreEvent(data)) return;
    state.bulkRestoreActive = false;
    state.lastCategoryWasMeasurement = false;
    state.manualOverride = null;
    setActive(false, 'auto:bulk-restore-complete');
  }

  /**
   * 🚨 A SILENTLY-SEEDED SLIDER DEFAULT IS NOT A CUSTOMER INTERACTION EITHER.
   * md-config-ui-horizontal-slider.js's seedSliderDefault() "clicks" Use
   * Default for Width/Height at bootstrap (apo_style + click-apply) by
   * calling the EXACT same commit path a real drag/click/track-tap takes, so
   * the SELECTION_BROWSING/SELECTION_APPLIED events it produces are
   * indistinguishable from a genuine one to evaluateForCategory below. Left
   * unguarded, loading the PDP alone silently pushed Width then Height
   * through, and since both are MEASUREMENT_MODE_CATEGORIES the toggle
   * engaged before the customer had touched anything — stealing the
   * carousel's first-slide view out from under them.
   *
   * A counter, not a boolean: unlike a bulk restoration (one START/COMPLETE
   * pair for the whole batch), each slider seeds independently and their
   * ~400ms+ debounce windows can overlap (Width still settling when Height's
   * seed starts). No explicit "reset to product view" on COMPLETED, unlike
   * handleBulkApplyCompleted — evaluateForCategory below simply never runs
   * while pendingDefaultSeeds > 0, so state.active is never touched by a
   * seed in the first place; there is nothing to reset.
   */
  function handleDefaultSeedStarted() {
    state.pendingDefaultSeeds += 1;
  }

  function handleDefaultSeedCompleted() {
    state.pendingDefaultSeeds = Math.max(0, state.pendingDefaultSeeds - 1);
  }

  /**
   * Re-evaluate which representation to show, for whatever category the customer
   * just touched.
   *
   * 🚨 THIS MUST RUN ON INTERACTION, NOT ONLY ON EXPANSION. It was wired to
   * ui:category_opened alone, which fires when a category EXPANDS. Every other
   * way of touching a category — clicking a tile inside one already open,
   * hovering an option, or a category the configurator auto-expanded — fired
   * only selection events, and those merely re-rendered without re-deciding.
   *
   * The result failed UAT from both directions at once: picking a wood left the
   * toggle stuck on measurements, and interacting with Width never moved it off
   * the picture. Both are the same missing re-evaluation, which is why fixing
   * only the symptom that was reported first would have left the other.
   */
  function evaluateForCategory(data, eventName) {
    const categoryId = data && (data.categoryId || data.category);
    if (!categoryId) return;

    // 🚨 A BULK RESTORATION IS NOT A CUSTOMER INTERACTION EITHER. While
    // handleBulkApplyStarted/Completed has the window open, every one of the
    // categories in a ?config= link (or a Will It Fit / simplified-mode
    // prefill) fires its own SELECTION_APPLIED, and letting each one re-decide
    // the toggle makes the FINAL on-screen state whichever category happened
    // to be applied last — for a shared link that is JSON key order, not
    // anything the customer chose. See handleBulkApplyCompleted for the
    // deliberate, single decision made once the bulk apply settles.
    if (state.bulkRestoreActive) return;

    // 🚨 NOR IS A SILENTLY-SEEDED SLIDER DEFAULT. See handleDefaultSeedStarted
    // above for the full incident this guards against.
    if (state.pendingDefaultSeeds > 0) return;

    /**
     * 🚨 ONLY THE CUSTOMER MOVES THIS TOGGLE. Ignore the system's own navigation.
     *
     * md-config-bl-auto-navigation-coordinator.js advances to the next category
     * after a selection is applied and publishes UI_CATEGORY_OPENED for it,
     * tagged source: 'auto_navigator'. So picking a wood produced TWO decisions
     * in quick succession — PICTURE for the wood, then TAPE because auto-nav had
     * moved on to a sizing category — and the toggle visibly flipped back and
     * forth on a single click.
     *
     * This is the cost of the previous fix: widening the subscription so tile
     * clicks re-evaluate also made the control responsive to navigation the
     * customer never asked for. The axis is a statement of intent, so only
     * user-initiated events may move it.
     */
    const src = data && data.source;
    const SOURCES = (CONSTANTS && CONSTANTS.EVENT_SOURCES) || {};
    const AUTO_NAV = SOURCES.AUTO_NAVIGATOR || 'auto_navigator';
    const SYSTEM_SRC = SOURCES.SYSTEM || 'system';

    /* 🚨 THE 'system' HALF OF THIS GUARD IS SCOPED TO UI_CATEGORY_OPENED, AND
       MUST STAY THAT WAY. It exists to stop the auto-navigator moving the
       toggle, and auto-navigation arrives as UI_CATEGORY_OPENED.

       Applied to EVERY event it silently broke the SLIDERS. Measured on the
       live dev theme: a tile click emits TWO events --
         selection:browsing  src=conductor   <- this is what decides the mode
         selection:applied   src=system
       while a Width/Height stepper press emits only ONE --
         selection:applied   src=system
       because the slider entered BROWSING back at its initial render, not on
       the press. EVERY apply is stamped 'system', user-initiated or not, so the
       blanket guard made selection:applied inert for mode decisions and the
       customer had to press twice: once for nothing, once for the event that
       finally got through.

       auto_navigator is still refused on ALL events -- that is a real statement
       of intent from a module, not a stamp every apply happens to carry. */
    const isSystemCategoryOpen =
      src === SYSTEM_SRC && eventName === 'UI_CATEGORY_OPENED';
    if (src === AUTO_NAV || isSystemCategoryOpen) {
      // Still repaint -- the drawing must reflect whatever just changed -- but
      // do not re-decide which side is showing.
      if (state.active) render();
      return;
    }

    const calc = window.MurphyDoorConfigurator.dimensionCalculator;
    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!calc) return;

    let classification = null;
    try {
      classification =
        conductor && typeof conductor.getCategoryClassification === 'function'
          ? conductor.getCategoryClassification(categoryId)
          : null;
    } catch (_) {
      classification = null;
    }

    // null = "leave it alone", the same contract detectAutoToggleNeeded uses for
    // the front/back axis. A category that is meaningful in BOTH representations
    // (Front Casing carries wall art AND a casing-to-casing dimension) must not
    // yank the customer out of the view they chose.
    const decision = calc.detectMeasurementModeNeeded(categoryId, classification);

    // Leaving the measurement group retires the manual override, so the customer
    // gets auto behaviour fresh next time rather than being stuck with a choice
    // they made several categories ago. `null` only arises from an explicit
    // 'both'/'none' metaobject override, which is not "leaving" anything — so
    // the override has to survive it.
    if (decision === false && state.lastCategoryWasMeasurement) {
      state.manualOverride = null;
    }
    if (decision !== null) state.lastCategoryWasMeasurement = decision;

    if (state.manualOverride !== null) {
      // Respect the explicit choice, but keep the drawing current underneath it.
      if (state.active) render();
      return;
    }

    if (decision === null) {
      // Explicit 'both'/'none' override: hold the representation, but re-render
      // so the drawing reflects whatever this category just changed.
      if (state.active) render();
      return;
    }

    // setActive() no-ops when the side is unchanged, so a selection inside the
    // category we are already showing would not repaint without this.
    if (decision === state.active) {
      if (state.active) {
        render();
        // Already in measurement mode -- just put the right frame up.
        selectFrameForCategory(categoryId);
      }
      return;
    }
    setActive(decision, 'auto:category-interaction');
    // setActive -> applyMode -> render is what BUILDS the frames, so the frame
    // choice has to come after it, not before.
    if (state.active) selectFrameForCategory(categoryId);
  }

  /** Space/Enter must operate a role=switch. A <button> gave this for free; an
   *  <svg tabindex=0> does not, and the Front/Back switch has the same gap. */
  function onToggleKeydown(e) {
    if (e.key !== ' ' && e.key !== 'Enter' && e.key !== 'Spacebar') return;
    e.preventDefault();
    onToggleClick();
  }

  function onToggleClick() {
    // One clock only. e.timeStamp is relative to page load while Date.now() is
    // epoch, and mixing the two makes the window either always or never elapsed.
    const now = Date.now();
    if (now - _lastToggleAt < GHOST_CLICK_WINDOW_MS) {
      isDev() && console.log('[BlueprintOverlay] ignored ghost click');
      return;
    }
    _lastToggleAt = now;
    state.manualOverride = !state.active;
    setActive(!state.active, 'user:toggle');
  }

  /**
   * Mirrors onToggleClick() exactly — same debounce clock, same
   * manualOverride write, same setActive() call — for a toggle REQUESTED by
   * a peer UI module (e.g. the mobile Traveling Preview sheet's mode control)
   * instead of a direct click on the physical .md-measure-toggle button.
   *
   * This is the only sanctioned way another module changes blueprint-overlay's
   * mode: it publishes UI_BLUEPRINT_MODE_TOGGLE_REQUESTED and this module
   * decides what to do about it, on its own clock, with its own guards.
   * A direct call into setActive() from outside this file would silently
   * skip the ghost-click window above, since _lastToggleAt is private state.
   */
  function onModeToggleRequested(data) {
    // Optional `active` boolean = "put measurement mode in THIS state" (the
    // mobile metrics dashboard's "See it on the plan" wants ON, never a flip
    // that could turn an already-on overlay OFF). Already there -> no-op, and
    // it does not consume the ghost-click window. Omitted = toggle, unchanged.
    const desired = data && typeof data.active === 'boolean' ? data.active : null;
    if (desired !== null && desired === state.active) return;
    const now = Date.now();
    if (now - _lastToggleAt < GHOST_CLICK_WINDOW_MS) {
      isDev() && console.log('[BlueprintOverlay] ignored ghost toggle-request');
      return;
    }
    _lastToggleAt = now;
    const next = desired !== null ? desired : !state.active;
    state.manualOverride = next;
    const source = (data && data.source) || 'requested:peer-module';
    setActive(next, source);
  }

  // ==========================================================================
  // Init
  // ==========================================================================

  function wireButton() {
    _button = document.querySelector('.md-measure-toggle');
    if (!_button) return false;
    _button.addEventListener('click', onToggleClick);
    // role=switch on a non-button element is not keyboard-operable for free.
    _button.addEventListener('keydown', onToggleKeydown);
    _button.setAttribute('data-initialized', 'true');
    cleanupHandlers.push(() => {
      if (!_button) return;
      _button.removeEventListener('click', onToggleClick);
      _button.removeEventListener('keydown', onToggleKeydown);
    });
    return true;
  }

  function wireViewObserver() {
    if (typeof window.MutationObserver !== 'function') return;
    const targets = [viewContainer('front'), viewContainer('back')].filter(Boolean);
    if (!targets.length) return;
    _viewObserver = new window.MutationObserver(() => {
      if (!state.active) return;
      const next = currentView();
      if (next === state.view) return;
      isDev() && console.log(`[BlueprintOverlay] view -> ${next}`);
      applyMode();
    });
    targets.forEach((t) =>
      _viewObserver.observe(t, { attributes: true, attributeFilter: ['style', 'aria-hidden'] })
    );
    cleanupHandlers.push(() => _viewObserver && _viewObserver.disconnect());
  }

  function wireResize() {
    const container = viewContainer('front');
    if (!container || typeof window.ResizeObserver !== 'function') return;
    // Text size is a function of container width, so a resize is a re-render.
    _resizeObserver = new window.ResizeObserver(() => {
      if (state.active) render();
    });
    _resizeObserver.observe(container);
    cleanupHandlers.push(() => _resizeObserver && _resizeObserver.disconnect());
  }

  function initialize() {
    if (state.initialized) return;
    try {
      if (!wireButton()) {
        isDev() && console.log('[BlueprintOverlay] no toggle in DOM - not a configurator page');
        return;
      }

      // Every signal that names a category re-runs the decision. Expansion is
      // only one of the ways a customer touches one.
      // The event NAME is threaded through: the source guard is scoped to
      // UI_CATEGORY_OPENED and cannot tell which event it is filtering on its own.
      subscribe(EVENTS.UI_CATEGORY_OPENED, (d) => evaluateForCategory(d, 'UI_CATEGORY_OPENED'));
      subscribe(EVENTS.SELECTION_APPLIED, (d) => evaluateForCategory(d, 'SELECTION_APPLIED'));
      subscribe(EVENTS.SELECTION_BROWSING, (d) => evaluateForCategory(d, 'SELECTION_BROWSING'));
      subscribe(EVENTS.UI_BROWSING_LAYER_SHOW, (d) => evaluateForCategory(d, 'UI_BROWSING_LAYER_SHOW'));
      // Tab switches carry no category, so they can only re-render.
      subscribe(EVENTS.UI_TAB_ACTIVATED, () => state.active && render());

      // A peer UI module (mobile Traveling Preview sheet) asking us to toggle
      // our own mode — see onModeToggleRequested for why this stays a request
      // a peer publishes, never a method a peer calls directly.
      subscribe(EVENTS.UI_BLUEPRINT_MODE_TOGGLE_REQUESTED, onModeToggleRequested);

      /**
       * 🚨 CLOSES A REAL, MEASURED SINGLE-FRAME FLASH OF THE CHIP-COLLISION BUG
       * ON EVERY SHEET CLOSE. md-config-ui-mobile-preview-sheet.js's
       * reparentSlideOut() moves the SAME .image-layers <li> back into the
       * tiny collapsed chip synchronously inside finishHide() -- but nothing
       * re-ran render() at that moment, so the DOM kept showing whatever was
       * last drawn while the container was full-sized inside the sheet (full
       * braces/labels) until wireResize()'s ResizeObserver eventually noticed
       * the box shrank and corrected it. Verified live by sampling state at
       * each browser paint opportunity (that sample point sees exactly what
       * is about to be painted next): one real paint landed with the slide
       * already back in the chip AND the old full-detail SVG still in place
       * -- the exact bug this whole file exists to fix, reappearing for a
       * frame on every close.
       *
       * finishHide() publishes this event synchronously, in the same task,
       * immediately after reparentSlideOut() returns -- before the browser
       * gets a chance to run layout/ResizeObserver/paint for that task at
       * all. Reacting to it here closes the gap to zero instead of depending
       * on a same-module ResizeObserver that was built for an unrelated
       * reason (container-width-driven text sizing) to incidentally catch
       * this. show()'s own finishShow-equivalent (reparentSlideIn, before
       * super.show()) publishes { open: true } the same way, so the open
       * path gets the identical correctness guarantee for free -- render()
       * on an inactive overlay (state.active false) is already a cheap
       * early-return, so firing on both open and close is not a concern.
       */
      subscribe(
        EVENTS.UI_MOBILE_PREVIEW_SHEET_STATE_CHANGED || 'ui:mobile_preview_sheet_state_changed',
        () => state.active && render()
      );

      // Bulk restoration window (?config= URL, Will It Fit prefill,
      // simplified-mode / gate-card defaults) — see handleBulkApplyStarted/
      // Completed for why the per-category auto-toggle is suppressed here.
      subscribe(EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED, handleBulkApplyStarted);
      subscribe(EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED, handleBulkApplyCompleted);

      // Silently-seeded slider default window (apo_style + click-apply
      // Width/Height at bootstrap) — see handleDefaultSeedStarted/Completed.
      subscribe(EVENTS.UI_SLIDER_DEFAULT_SEED_STARTED, handleDefaultSeedStarted);
      subscribe(EVENTS.UI_SLIDER_DEFAULT_SEED_COMPLETED, handleDefaultSeedCompleted);

      /**
       * Pass-through arrives from md-config-bl-space-calculator, the module that
       * owns the in-swing pass-through (sheet B24) and the OUT_SWING_SPECS table.
       * Its `diagram` block exists precisely so consumers annotate from these
       * numbers rather than recomputing them -- that module's own comment
       * records that they "got re-derived in multiple places and drifted".
       *
       * 🚨 THIS EVENT IS PUBLISHED WITH TWO DIFFERENT PAYLOAD SHAPES.
       * calculateDoorSpecs emits {doorWidth, doorDepth, doorType, result, ...};
       * coordinateSpaceUpdate emits {data: spaceDisplayData, ...}. Only the
       * second carries the diagram block, and only it runs on a selection
       * change. Reading the wrong key here would not throw -- it would leave
       * spaceData null forever and silently drop the pass-through span, the
       * same failure mode as listening on a channel nobody speaks.
       */
      subscribe(EVENTS.BUSINESS_SPACE_CALCULATION_COMPLETED, (payload) => {
        const diagram = payload && payload.data && payload.data.diagram;
        if (!diagram) return; // the inner calculateDoorSpecs shape; not ours
        state.spaceData = diagram;
        if (state.active) render();
      });

      // ORTHOGONALITY CONTRACT. Two independent axes:
      //   front/back        — owned by media-gallery.js setView() and
      //                       md-config-bl-image-layer-coordinator.js
      //                       (detectAutoToggleNeeded -> ui:auto_toggle_view)
      //   measure/product   — owned here (detectMeasurementModeNeeded)
      // Neither writes the other's state. We only OBSERVE the face so the
      // blueprint redraws with the right trims and traces; we never flip it, and
      // the layer coordinator never touches mode. All four combinations
      // (front+measure, front+product, back+measure, back+product) are reachable.
      //
      // Observed, not subscribed. setView() flips inline visibility/aria-hidden
      // and publishes no DOM event for it, so a MutationObserver on the attribute
      // it actually writes is the only signal that cannot silently stop arriving.
      wireViewObserver();

      wireResize();

      state.initialized = true;
      publish(EVENTS.UI_BLUEPRINT_OVERLAY_READY || 'ui:blueprint_overlay_ready', {
        module: 'md-config-ui-blueprint-overlay',
        timestamp: Date.now(),
      });
      isDev() && console.log('[BlueprintOverlay] initialized');
    } catch (error) {
      isDev() && console.error('[BlueprintOverlay] initialization failed:', error);
    }
  }

  function cleanup() {
    cleanupHandlers.forEach((off) => {
      try { off(); } catch (err) { isDev() && console.error('[BlueprintOverlay] cleanup error', err); }
    });
    cleanupHandlers.length = 0;
    state.initialized = false;
    state.active = false;
    state.manualOverride = null;
    state.bulkRestoreActive = false;
    state.pendingDefaultSeeds = 0;
    // Cached from a peer module, so it must not outlive this instance.
    state.spaceData = null;
    _lastToggleAt = 0;
    _button = null;
    _resizeObserver = null;
    _viewObserver = null;
    setFrameSwitchVisible(false);
    _framesEl = null;
    _frameSwitch = null;
    state.frame = 'elevation';
  }

  window.MurphyDoorConfigurator.blueprintOverlay = {
    initialize,
    cleanup,
    setActive,
    render,
    _state: state, // test seam
    _showFrame: showFrame, // test seam — drives the frame switch without a click
    _onToggleClick: onToggleClick, // test seam — exercises the ghost-click guard
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})(window);
